/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import type {
  BubbleDataPoint,
  ChartData,
  ChartDataset,
  ChartOptions,
  Point,
} from 'chart.js';
import type { TFunction } from 'i18next';
import type { sessions } from '@prisma/client';
import { COLORS } from './commons';
import { get_percentage } from './utils/utils-math';
import {
  formatSessionDate,
  sessionDateKey,
} from './utils/timezone-utils';
import i18next from 'i18next';
import { DeviceModelType } from './enums';
import { AvailableResourceActions, can, Role } from './role-constants';

// Steps graph = MODE - LINEAR
// Time graph = MODE - LINEAR
//==============PROCESS FUNCTIONS OF DATA============================//
/**
 * Process a list of sessions to group them by date and merge the data
 *
 * @export
 * @param {any[]} sessions
 * @return {*}  {any[]}
 *
 */
export function processSessions(sessions: any[]): any[] {
  const groupedSessions = sessions.reduce(
    (acc, session) => {
      const sessionDate = session.date.toISOString().split('T')[0];
      if (!acc[sessionDate]) {
        acc[sessionDate] = [];
      }
      acc[sessionDate].push(session);
      return acc;
    },
    {} as Record<string, any[]>
  );

  const mergedSessions = (Object.values(groupedSessions) as any[][])
    .map((group: any[]) => {
      return group.reduce(
        (merged, session) => {
          // Sumar los pasos y tiempos
          merged.steps_automatic_forward +=
            session.steps_automatic_forward ?? 0;
          merged.steps_automatic_backward +=
            session.steps_automatic_backward ?? 0;
          merged.steps_intention_forward +=
            session.steps_intention_forward ?? 0;
          merged.steps_intention_backward +=
            session.steps_intention_backward ?? 0;
          merged.time_automatic_forward += session.time_automatic_forward ?? 0;
          merged.time_automatic_backward +=
            session.time_automatic_backward ?? 0;
          merged.time_intention_forward += session.time_intention_forward ?? 0;
          merged.time_intention_backward +=
            session.time_intention_backward ?? 0;
          //Añadidas nuevas modos AAN1 y AAN2
          merged.steps_aan1_forward += session.steps_aan1_forward ?? 0;
          merged.steps_aan1_backward += session.steps_aan1_backward ?? 0;
          merged.steps_aan2_forward += session.steps_aan2_forward ?? 0;
          merged.steps_aan2_backward += session.steps_aan2_backward ?? 0;
          merged.time_aan1_forward += session.time_aan1_forward ?? 0;
          merged.time_aan1_backward += session.time_aan1_backward ?? 0;
          merged.time_aan2_forward += session.time_aan2_forward ?? 0;
          merged.time_aan2_backward += session.time_aan2_backward ?? 0;
          //
          merged.steps_total += session.steps_total ?? 0;
          merged.time_total += session.time_total ?? 0;
          merged.timeWalking += session.timeWalking ?? 0;
          merged.timeUse += session.timeUse ?? 0;
          merged.timeSession += session.timeSession ?? 0;
          merged.so2 += session.so2;
          merged.fc_max = Math.max(merged.fc_max, session.fc_max ?? 0);
          merged.fc_med += session.fc_med;
          // ROM: Tomar el valor máximo de las sesiones
          merged.rom_hipl = Math.max(merged.rom_hipl, session.rom_hipl ?? 0);
          merged.rom_hipr = Math.max(merged.rom_hipr, session.rom_hipr ?? 0);
          merged.rom_kneel = Math.max(merged.rom_kneel, session.rom_kneel ?? 0);
          merged.rom_kneer = Math.max(merged.rom_kneer, session.rom_kneer ?? 0);
          merged.torque_hipl += safeNumber(session.torque_hipl);
          merged.torque_hipr += safeNumber(session.torque_hipr);
          merged.torque_kneel += safeNumber(session.torque_kneel);
          merged.torque_kneer += safeNumber(session.torque_kneer);
          merged.flexos_hip = // Otros cálculos (mínimos y máximos)
            Math.min(merged.flexos_hip, session.flexos_hip);
          merged.flexos_knee = Math.min(
            merged.flexos_knee,
            session.flexos_knee
          );
          merged.flexos_ankle = Math.min(
            merged.flexos_ankle,
            session.flexos_ankle
          );
          merged.threshold_hipl = Math.max(
            merged.threshold_hipl,
            session.threshold_hipl
          );
          merged.threshold_kneel = Math.max(
            merged.threshold_kneel,
            session.threshold_kneel
          );
          merged.threshold_hipr = Math.max(
            merged.threshold_hipr,
            session.threshold_hipr
          );
          merged.threshold_kneer = Math.max(
            merged.threshold_kneer,
            session.threshold_kneer
          );

          merged.end_time = merged.end_time
            ? new Date(
              Math.max(
                merged.end_time.getTime(),
                session.end_time ? session.end_time.getTime() : -Infinity
              )
            )
            : session.end_time;
          merged.date = merged.date
            ? new Date(Math.min(merged.date.getTime(), session.date.getTime()))
            : session.date;
          merged.id = merged.id ?? session.id;
          merged.created_at = merged.created_at
            ? new Date(
              Math.min(
                merged.created_at.getTime(),
                session.created_at.getTime()
              )
            )
            : session.created_at;
          merged.updated_at = merged.updated_at
            ? new Date(
              Math.max(
                merged.updated_at.getTime(),
                session.updated_at.getTime()
              )
            )
            : session.updated_at;
          merged.effort = session.effort ?? merged.effort;
          merged.d_id = merged.d_id ?? session.d_id;
          merged.p_id = merged.p_id ?? session.p_id;
          merged.evaluation += session.evaluation ?? 0;
          merged.i_id = merged.i_id ?? session.i_id;
          merged.session_count += 1;
          // Propagar device_model
          merged.device_model =
            merged.device_model ?? session.devices?.model ?? null;

          return merged;
        },
        {
          steps_automatic_forward: 0,
          steps_automatic_backward: 0,
          steps_intention_forward: 0,
          steps_intention_backward: 0,
          steps_aan1_forward: 0,
          steps_aan1_backward: 0,
          steps_aan2_forward: 0,
          steps_aan2_backward: 0,
          time_automatic_forward: 0,
          time_automatic_backward: 0,
          time_intention_forward: 0,
          time_intention_backward: 0,
          time_aan1_forward: 0,
          time_aan1_backward: 0,
          time_aan2_forward: 0,
          time_aan2_backward: 0,
          steps_total: 0,
          time_total: 0,
          timeWalking: 0,
          timeUse: 0,
          timeSession: 0,
          rom_hipl: 0,
          rom_hipr: 0,
          rom_kneel: 0,
          rom_kneer: 0,
          torque_hipl: 0,
          torque_hipr: 0,
          torque_kneel: 0,
          torque_kneer: 0,
          flexos_hip: Infinity,
          flexos_knee: Infinity,
          flexos_ankle: Infinity,
          threshold_hipl: -Infinity,
          threshold_kneel: -Infinity,
          threshold_hipr: -Infinity,
          threshold_kneer: -Infinity,
          fc_max: 0,
          fc_med: 0,
          so2: 0,
          end_time: null,
          date: null,
          id: '',
          created_at: null,
          updated_at: null,
          effort: 0,
          d_id: null,
          p_id: '',
          evaluation: null,
          i_id: null,
          session_count: 0,
          device_model: null,
        }
      );
    })
    .map((session) => {
      session.torque_hipl /= session.session_count;
      session.torque_hipr /= session.session_count;
      session.torque_kneel /= session.session_count;
      session.torque_kneer /= session.session_count;
      session.so2 = session.so2 / session.session_count;
      session.fc_med = session.fc_med / session.session_count;

      session.cadence_automatic_forward =
        session.time_automatic_forward && session.time_automatic_forward > 0
          ? session.steps_automatic_forward / session.time_automatic_forward
          : 0;

      session.cadence_automatic_backward =
        session.time_automatic_backward && session.time_automatic_backward > 0
          ? session.steps_automatic_backward / session.time_automatic_backward
          : 0;

      session.cadence_intention_forward =
        session.time_intention_forward && session.time_intention_forward > 0
          ? session.steps_intention_forward / session.time_intention_forward
          : 0;

      session.cadence_intention_backward =
        session.time_intention_backward && session.time_intention_backward > 0
          ? session.steps_intention_backward / session.time_intention_backward
          : 0;

      return session;
    });

  return mergedSessions;
}

/**
 * Process a list of sessions to group them by patient and merge the data
 *
 * @export
 * @param {any[]} sessions
 * @return {*}  {any[]}
 *
 */
export function processSessionsByPatient(sessions: any[]): any[] {
  const groupedSessions = sessions.reduce(
    (acc, session) => {
      const sessionDate = session.date.toISOString().split('T')[0];
      const key = `${session.p_id}-${sessionDate}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(session);
      return acc;
    },
    {} as Record<string, any[]>
  );

  const mergedSessions = (Object.values(groupedSessions) as any[][])
    .map((group: any[]) => {
      return group.reduce(
        (merged, session) => {
          merged.steps_automatic_forward +=
            session.steps_automatic_forward ?? 0;
          merged.steps_automatic_backward +=
            session.steps_automatic_backward ?? 0;
          merged.steps_intention_forward +=
            session.steps_intention_forward ?? 0;
          merged.steps_intention_backward +=
            session.steps_intention_backward ?? 0;
          merged.steps_aan1_forward += session.steps_aan1_forward ?? 0;
          merged.steps_aan1_backward += session.steps_aan1_backward ?? 0;
          merged.steps_aan2_forward += session.steps_aan2_forward ?? 0;
          merged.steps_aan2_backward += session.steps_aan2_backward ?? 0;

          merged.time_automatic_forward += session.time_automatic_forward ?? 0;
          merged.time_automatic_backward +=
            session.time_automatic_backward ?? 0;
          merged.time_intention_forward += session.time_intention_forward ?? 0;
          merged.time_intention_backward +=
            session.time_intention_backward ?? 0;
          merged.time_aan1_forward += session.time_aan1_forward ?? 0;
          merged.time_aan1_backward += session.time_aan1_backward ?? 0;
          merged.time_aan2_forward += session.time_aan2_forward ?? 0;
          merged.time_aan2_backward += session.time_aan2_backward ?? 0;

          merged.steps_total += session.steps_total ?? 0;
          merged.time_total += session.time_total ?? 0;
          merged.timeWalking += session.timeWalking ?? 0;
          merged.timeUse += session.timeUse ?? 0;
          merged.timeSession += session.timeSession ?? 0;
          merged.so2 += session.so2;
          merged.fc_max = Math.max(merged.fc_max, session.fc_max ?? 0);
          merged.fc_med += session.fc_med;
          merged.torque_hipl += safeNumber(session.torque_hipl);
          merged.torque_hipr += safeNumber(session.torque_hipr);
          merged.torque_kneel += safeNumber(session.torque_kneel);
          merged.torque_kneer += safeNumber(session.torque_kneer);
          merged.flexos_hip = Math.min(merged.flexos_hip, session.flexos_hip);
          merged.flexos_knee = Math.min(
            merged.flexos_knee,
            session.flexos_knee
          );
          merged.flexos_ankle = Math.min(
            merged.flexos_ankle,
            session.flexos_ankle
          );
          merged.threshold_hipl = Math.max(
            merged.threshold_hipl,
            session.threshold_hipl
          );
          merged.threshold_kneel = Math.max(
            merged.threshold_kneel,
            session.threshold_kneel
          );
          merged.threshold_hipr = Math.max(
            merged.threshold_hipr,
            session.threshold_hipr
          );
          merged.threshold_kneer = Math.max(
            merged.threshold_kneer,
            session.threshold_kneer
          );

          merged.end_time = merged.end_time
            ? new Date(
              Math.max(
                merged.end_time.getTime(),
                session.end_time ? session.end_time.getTime() : -Infinity
              )
            )
            : session.end_time;
          // Actualizamos la fecha al valor más antiguo encontrado
          merged.date = merged.date
            ? new Date(Math.min(merged.date.getTime(), session.date.getTime()))
            : session.date;
          // Para valores que no deben ser combinados, simplemente usamos el primero del grupo
          merged.id = merged.id ?? session.id;
          merged.created_at = merged.created_at
            ? new Date(
              Math.min(
                merged.created_at.getTime(),
                session.created_at.getTime()
              )
            )
            : session.created_at;
          merged.updated_at = merged.updated_at
            ? new Date(
              Math.max(
                merged.updated_at.getTime(),
                session.updated_at.getTime()
              )
            )
            : session.updated_at;
          merged.effort = session.effort ?? merged.effort;
          merged.d_id = merged.d_id ?? session.d_id;
          merged.p_id = merged.p_id ?? session.p_id;
          merged.evaluation = session.evaluation ?? merged.evaluation;
          merged.i_id = merged.i_id ?? session.i_id;
          return merged;
        },
        {
          steps_automatic_forward: 0,
          steps_automatic_backward: 0,
          steps_intention_forward: 0,
          steps_intention_backward: 0,
          steps_aan1_forward: 0,
          steps_aan1_backward: 0,
          steps_aan2_forward: 0,
          steps_aan2_backward: 0,
          time_automatic_forward: 0,
          time_automatic_backward: 0,
          time_intention_forward: 0,
          time_intention_backward: 0,
          time_aan1_forward: 0,
          time_aan1_backward: 0,
          time_aan2_forward: 0,
          time_aan2_backward: 0,
          steps_total: 0,
          time_total: 0,
          timeWalking: 0,
          timeUse: 0,
          timeSession: 0,
          rom_hipl: 0,
          rom_hipr: 0,
          rom_kneel: 0,
          rom_kneer: 0,
          torque_hipl: 0,
          torque_hipr: 0,
          torque_kneel: 0,
          torque_kneer: 0,
          flexos_hip: Infinity,
          flexos_knee: Infinity,
          flexos_ankle: Infinity,
          threshold_hipl: -Infinity,
          threshold_kneel: -Infinity,
          threshold_hipr: -Infinity,
          threshold_kneer: -Infinity,
          fc_max: 0,
          fc_med: 0,
          so2: 0,
          end_time: null,
          date: null,
          id: '',
          created_at: null,
          updated_at: null,
          effort: 0,
          d_id: null,
          p_id: '',
          evaluation: null,
          i_id: null,
          session_count: 0,
          device_model: null,
        }
      );
    })
    .map((session) => {
      session.torque_hipl /= session.session_count;
      session.torque_hipr /= session.session_count;
      session.torque_kneel /= session.session_count;
      session.torque_kneer /= session.session_count;
      session.so2 = session.so2 / session.session_count;
      session.fc_med = session.fc_med / session.session_count;

      session.cadence_automatic_forward =
        session.time_automatic_forward && session.time_automatic_forward > 0
          ? session.steps_automatic_forward / session.time_automatic_forward
          : 0;

      session.cadence_automatic_backward =
        session.time_automatic_backward && session.time_automatic_backward > 0
          ? session.steps_automatic_backward / session.time_automatic_backward
          : 0;

      session.cadence_intention_forward =
        session.time_intention_forward && session.time_intention_forward > 0
          ? session.steps_intention_forward / session.time_intention_forward
          : 0;

      session.cadence_intention_backward =
        session.time_intention_backward && session.time_intention_backward > 0
          ? session.steps_intention_backward / session.time_intention_backward
          : 0;

      return session;
    });

  return mergedSessions;
}

/**
 * Get sessions filtered by date
 *
 * @export
 * @param {any[]} sessions
 * @return {*}  {SessionData}
 */

export function getSessionsFilteredByDate(
  sessions: any[],
  timeZone?: string
): any[] {
  const sessionsByDate: any = {};

  sessions.forEach((session) => {
    const date = sessionDateKey(session.date, timeZone);

    if (!sessionsByDate[date]) {
      // Initialize with the first session of that date
      sessionsByDate[date] = { ...session };
    } else {
      // Aggregate data for sessions of the same date
      const aggregatedSession = sessionsByDate[date];
      Object.keys(session).forEach((key) => {
        if (typeof session[key] === 'number') {
          // Sum numerical values
          aggregatedSession[key] = (aggregatedSession[key] ?? 0) + session[key];
        } else if (
          key === 'created_at' ||
          key === 'updated_at' ||
          key === 'start_time' ||
          key === 'end_time'
        ) {
          // Keep the most recent non-null timestamp
          if (new Date(session[key]) > new Date(aggregatedSession[key])) {
            aggregatedSession[key] = session[key];
          }
        }
        // For non-numerical and non-timestamp fields, we'll keep the last non-null value
        else if (session[key] !== null) {
          aggregatedSession[key] = session[key];
        }
      });
    }
  });

  // Convert dictionary to array
  return Object.values(sessionsByDate);
}

/**
 * Get the total of calculated sessions
 *
 * @export
 * @param {any[]} sessions
 * @return {*}  {SessionData}
 */
export function getTotalsOfSessions(sessions: any[]): SessionData {
  let total_steps_automatic_forward = 0;
  let total_steps_automatic_backward = 0;
  let total_steps_intention_forward = 0;
  let total_steps_intention_backward = 0;
  let total_steps_aan1_forward = 0;
  let total_steps_aan1_backward = 0;
  let total_steps_aan2_forward = 0;
  let total_steps_aan2_backward = 0;
  let total_steps_automatic = 0;
  let total_steps_intention = 0;
  let total_steps_aan1 = 0;
  let total_steps_aan2 = 0;
  let total_steps_forward = 0;
  let total_steps_backward = 0;

  let total_time_automatic_forward = 0;
  let total_time_automatic_backward = 0;
  let total_time_intention_forward = 0;
  let total_time_intention_backward = 0;
  let total_time_aan1_forward = 0;
  let total_time_aan1_backward = 0;
  let total_time_aan2_forward = 0;
  let total_time_aan2_backward = 0;
  let total_time_automatic = 0;
  let total_time_intention = 0;
  let total_time_aan1 = 0;
  let total_time_aan2 = 0;
  let total_time_forward = 0;
  let total_time_backward = 0;

  let total_steps_total = 0;
  let total_time_total = 0;

  let total_time_use = 0;
  let total_time_walking = 0;
  let total_time_standing = 0;

  // Median

  let median_total_steps = 0;
  let median_total_steps_automatic_forward = 0;
  let median_total_steps_automatic_backward = 0;
  let median_total_steps_intention_forward = 0;
  let median_total_steps_intention_backward = 0;
  let median_total_steps_automatic = 0;
  let median_total_steps_intention = 0;

  let median_total_steps_forward = 0;
  let median_total_steps_backward = 0;

  let median_total_time = 0;
  let median_total_time_automatic_forward = 0;
  let median_total_time_automatic_backward = 0;
  let median_total_time_intention_forward = 0;
  let median_total_time_intention_backward = 0;

  let median_total_time_automatic = 0;
  let median_total_time_intention = 0;
  let median_total_time_forward = 0;
  let median_total_time_backward = 0;
  let evaluation = 0;
  let cadence_automatic_forward = 0;
  let cadence_automatic_backward = 0;
  let cadence_intention_forward = 0;
  let cadence_intention_backward = 0;
  let flexos_hip = 0;
  let flexos_knee = 0;
  let flexos_ankle = 0;
  let threshold_hipl = 0;
  let threshold_hipr = 0;
  let threshold_kneel = 0;
  let threshold_kneer = 0;

  let effort = 0;
  let torque_hipltotal = 0;
  let torque_hiprtotal = 0;
  let torque_kneeltotal = 0;
  let torque_kneertotal = 0;
  let rom_hipltotal = 0;
  let rom_hiprtotal = 0;
  let rom_kneeltotal = 0;
  let rom_kneertotal = 0;
  let fc_max = 0;
  let fc_med = 0;
  let so2 = 0;

  sessions.forEach((session, idx) => {
    total_steps_automatic_forward += session.steps_automatic_forward ?? 0;
    total_steps_automatic_backward += session.steps_automatic_backward ?? 0;
    total_steps_intention_forward += session.steps_intention_forward ?? 0;
    total_steps_intention_backward += session.steps_intention_backward ?? 0;
    total_time_automatic_forward += session.time_automatic_forward ?? 0;
    total_time_automatic_backward += session.time_automatic_backward ?? 0;
    total_time_intention_forward += session.time_intention_forward ?? 0;
    total_time_intention_backward += session.time_intention_backward ?? 0;

    // NUEVOS MODO ANN1 Y ANN2
    total_steps_aan1_forward += session.steps_aan1_forward ?? 0;
    total_steps_aan1_backward += session.steps_aan1_backward ?? 0;
    total_steps_aan2_forward += session.steps_aan2_forward ?? 0;
    total_steps_aan2_backward += session.steps_aan2_backward ?? 0;

    total_time_aan1_forward += session.time_aan1_forward ?? 0;
    total_time_aan1_backward += session.time_aan1_backward ?? 0;
    total_time_aan2_forward += session.time_aan2_forward ?? 0;
    total_time_aan2_backward += session.time_aan2_backward ?? 0;

    total_time_total += session.time_total;
    total_time_use += session.timeUse;
    total_time_walking += session.timeWalking;
    total_time_standing += session.timeSession;
    evaluation += session.evaluation;
    cadence_automatic_forward += session.cadence_automatic_forward;
    cadence_automatic_backward += session.cadence_automatic_backward;
    cadence_intention_forward += session.cadence_intention_forward;
    cadence_intention_backward += session.cadence_intention_backward;
    flexos_hip = Math.min(flexos_hip, session.flexos_hip);
    flexos_knee = Math.min(flexos_knee, session.flexos_knee);
    flexos_ankle = Math.min(flexos_ankle, session.flexos_ankle);
    threshold_hipl = Math.max(threshold_hipl, session.threshold_hipl);
    threshold_hipr = Math.max(threshold_hipr, session.threshold_hipr);
    threshold_kneel = Math.max(threshold_kneel, session.threshold_kneel);
    threshold_kneer = Math.max(threshold_kneer, session.threshold_kneer);
    effort = Math.max(effort, session.effort);
    torque_hipltotal += session.torque_hipl;
    torque_hiprtotal += session.torque_hipr;
    torque_kneeltotal += session.torque_kneel;
    torque_kneertotal += session.torque_kneer;
    rom_hipltotal += session.rom_hipl;
    rom_hiprtotal += session.rom_hipr;
    rom_kneeltotal += session.rom_kneel;
    rom_kneertotal += session.rom_kneer;
    fc_max = Math.max(fc_max, session.fc_max);
    fc_med += session.fc_med;
    so2 += session.so2;
  });

  // Totals steps
  total_steps_automatic =
    total_steps_automatic_forward + total_steps_automatic_backward;
  total_steps_intention =
    total_steps_intention_forward + total_steps_intention_backward;
  total_steps_forward =
    total_steps_automatic_forward + total_steps_intention_forward;
  total_steps_backward =
    total_steps_automatic_backward + total_steps_intention_backward;

  // NUEVOS MODOS total steps ANN1 Y ANN2
  total_steps_aan1 = total_steps_aan1_forward + total_steps_aan1_backward;
  total_steps_aan2 = total_steps_aan2_forward + total_steps_aan2_backward;

  total_steps_total =
    total_steps_automatic +
    total_steps_intention +
    total_steps_aan1 +
    total_steps_aan2;

  // Totals time
  total_time_automatic =
    total_time_automatic_forward + total_time_automatic_backward;
  total_time_intention =
    total_time_intention_forward + total_time_intention_backward;
  total_time_forward =
    total_time_automatic_forward + total_time_intention_forward;
  total_time_backward =
    total_time_automatic_backward + total_time_intention_backward;

  // NUEVOS MODOS para time ANN1 Y ANN2
  total_time_aan1 = total_time_aan1_forward + total_time_aan1_backward;
  total_time_aan2 = total_time_aan2_forward + total_time_aan2_backward;

  total_time_total =
    total_time_automatic +
    total_time_intention +
    total_time_aan1 +
    total_time_aan2;

  if (sessions.length > 0) {
    median_total_steps = total_steps_total / sessions.length;
    median_total_steps_automatic_forward =
      total_steps_automatic_forward / sessions.length;
    median_total_steps_automatic_backward =
      total_steps_automatic_backward / sessions.length;
    median_total_steps_intention_forward =
      total_steps_intention_forward / sessions.length;
    median_total_steps_intention_backward =
      total_steps_intention_backward / sessions.length;
    median_total_steps_automatic = total_steps_automatic / sessions.length;
    median_total_steps_intention = total_steps_intention / sessions.length;

    median_total_steps_forward = total_steps_forward / sessions.length;
    median_total_steps_backward = total_steps_backward / sessions.length;
    // Time
    median_total_time = total_time_use / sessions.length;
    median_total_time_automatic_forward =
      total_time_automatic_forward / sessions.length;
    median_total_time_automatic_backward =
      total_time_automatic_backward / sessions.length;
    median_total_time_intention_forward =
      total_time_intention_forward / sessions.length;
    median_total_time_intention_backward =
      total_time_intention_backward / sessions.length;
    median_total_time_automatic = total_time_automatic / sessions.length;
    median_total_time_intention = total_time_intention / sessions.length;
    median_total_time_forward = total_time_forward / sessions.length;
    median_total_time_backward = total_time_backward / sessions.length;

    cadence_automatic_forward = cadence_automatic_forward / sessions.length;
    cadence_automatic_backward = cadence_automatic_backward / sessions.length;
    cadence_intention_forward = cadence_intention_forward / sessions.length;
    cadence_intention_backward = cadence_intention_backward / sessions.length;
    fc_med = fc_med / sessions.length;
    so2 = so2 / sessions.length;
  }

  return {
    // steps base
    total_steps_automatic_forward: parseFloat(
      total_steps_automatic_forward.toFixed(2)
    ),
    total_steps_automatic_backward: parseFloat(
      total_steps_automatic_backward.toFixed(2)
    ),
    total_steps_intention_forward: parseFloat(
      total_steps_intention_forward.toFixed(2)
    ),
    total_steps_intention_backward: parseFloat(
      total_steps_intention_backward.toFixed(2)
    ),

    // steps ANN1/ANN2 (faltaban)
    total_steps_aan1_forward: parseFloat(total_steps_aan1_forward.toFixed(2)),
    total_steps_aan1_backward: parseFloat(total_steps_aan1_backward.toFixed(2)),
    total_steps_aan2_forward: parseFloat(total_steps_aan2_forward.toFixed(2)),
    total_steps_aan2_backward: parseFloat(total_steps_aan2_backward.toFixed(2)),
    total_steps_aan1: parseFloat(total_steps_aan1.toFixed(2)),
    total_steps_aan2: parseFloat(total_steps_aan2.toFixed(2)),

    total_steps_automatic: parseFloat(total_steps_automatic.toFixed(2)),
    total_steps_intention: parseFloat(total_steps_intention.toFixed(2)),
    total_steps_forward: parseFloat(total_steps_forward.toFixed(2)),
    total_steps_backward: parseFloat(total_steps_backward.toFixed(2)),
    total_steps_total: parseFloat(total_steps_total.toFixed(2)), // faltaba

    // time base (faltaban muchos)
    total_time_automatic_forward: parseFloat(
      total_time_automatic_forward.toFixed(2)
    ),
    total_time_automatic_backward: parseFloat(
      total_time_automatic_backward.toFixed(2)
    ),
    total_time_intention_forward: parseFloat(
      total_time_intention_forward.toFixed(2)
    ),
    total_time_intention_backward: parseFloat(
      total_time_intention_backward.toFixed(2)
    ),

    // time ANN1/ANN2
    total_time_aan1_forward: parseFloat(total_time_aan1_forward.toFixed(2)),
    total_time_aan1_backward: parseFloat(total_time_aan1_backward.toFixed(2)),
    total_time_aan2_forward: parseFloat(total_time_aan2_forward.toFixed(2)),
    total_time_aan2_backward: parseFloat(total_time_aan2_backward.toFixed(2)),
    total_time_aan1: parseFloat(total_time_aan1.toFixed(2)),
    total_time_aan2: parseFloat(total_time_aan2.toFixed(2)),

    total_time_automatic: parseFloat(total_time_automatic.toFixed(2)),
    total_time_intention: parseFloat(total_time_intention.toFixed(2)),
    total_time_forward: parseFloat(total_time_forward.toFixed(2)),
    total_time_backward: parseFloat(total_time_backward.toFixed(2)),
    total_time_total: parseFloat(total_time_total.toFixed(2)),

    total_time_use: parseFloat(total_time_use.toFixed(2)),
    total_time_walking: parseFloat(total_time_walking.toFixed(2)),
    total_time_standing:
      parseFloat(total_time_standing.toFixed(2)) -
      parseFloat(total_time_walking.toFixed(2)),

    evaluation: evaluation,

    // median steps/time (los que ya tenías)
    median_total_steps: parseFloat(median_total_steps.toFixed(2)),
    median_total_steps_automatic_forward: parseFloat(
      median_total_steps_automatic_forward.toFixed(2)
    ),
    median_total_steps_automatic_backward: parseFloat(
      median_total_steps_automatic_backward.toFixed(2)
    ),
    median_total_steps_intention_forward: parseFloat(
      median_total_steps_intention_forward.toFixed(2)
    ),
    median_total_steps_intention_backward: parseFloat(
      median_total_steps_intention_backward.toFixed(2)
    ),
    median_total_steps_automatic: parseFloat(
      median_total_steps_automatic.toFixed(2)
    ),
    median_total_steps_intention: parseFloat(
      median_total_steps_intention.toFixed(2)
    ),
    median_total_steps_forward: parseFloat(
      median_total_steps_forward.toFixed(2)
    ),
    median_total_steps_backward: parseFloat(
      median_total_steps_backward.toFixed(2)
    ),

    median_total_time: parseFloat(median_total_time.toFixed(2)),
    median_total_time_automatic_forward: parseFloat(
      median_total_time_automatic_forward.toFixed(2)
    ),
    median_total_time_automatic_backward: parseFloat(
      median_total_time_automatic_backward.toFixed(2)
    ),
    median_total_time_intention_forward: parseFloat(
      median_total_time_intention_forward.toFixed(2)
    ),
    median_total_time_intention_backward: parseFloat(
      median_total_time_intention_backward.toFixed(2)
    ),
    median_total_time_automatic: parseFloat(
      median_total_time_automatic.toFixed(2)
    ),
    median_total_time_intention: parseFloat(
      median_total_time_intention.toFixed(2)
    ),
    median_total_time_forward: parseFloat(median_total_time_forward.toFixed(2)),
    median_total_time_backward: parseFloat(
      median_total_time_backward.toFixed(2)
    ),

    cadence_automatic_forward: parseFloat(cadence_automatic_forward.toFixed(2)),
    cadence_automatic_backward: parseFloat(
      cadence_automatic_backward.toFixed(2)
    ),
    cadence_intention_forward: parseFloat(cadence_intention_forward.toFixed(2)),
    cadence_intention_backward: parseFloat(
      cadence_intention_backward.toFixed(2)
    ),

    flexos_hip: parseFloat(flexos_hip.toFixed(2)),
    flexos_knee: parseFloat(flexos_knee.toFixed(2)),
    flexos_ankle: parseFloat(flexos_ankle.toFixed(2)),
    threshold_hipl: parseFloat(threshold_hipl.toFixed(2)),
    threshold_hipr: parseFloat(threshold_hipr.toFixed(2)),
    threshold_kneel: parseFloat(threshold_kneel.toFixed(2)),
    threshold_kneer: parseFloat(threshold_kneer.toFixed(2)),

    effort: parseFloat(effort.toFixed(2)),
    torque_hipl: parseFloat((torque_hipltotal / 100).toFixed(2)),
    torque_hipr: parseFloat((torque_hiprtotal / 100).toFixed(2)),
    torque_kneel: parseFloat((torque_kneeltotal / 100).toFixed(2)),
    torque_kneer: parseFloat((torque_kneertotal / 100).toFixed(2)),
    rom_hipl: parseFloat((rom_hipltotal / 100).toFixed(2)),
    rom_hipr: parseFloat((rom_hiprtotal / 100).toFixed(2)),
    rom_kneel: parseFloat((rom_kneeltotal / 100).toFixed(2)),
    rom_kneer: parseFloat((rom_kneertotal / 100).toFixed(2)),

    fc_max: parseFloat(fc_max.toFixed(2)),
    fc_med: parseFloat(fc_med.toFixed(2)),
    so2: parseFloat(so2.toFixed(2)),
  };
}

/*Grafica para sesion combiada por modo y direccion*/
// Calcula los totales de una sola sesión agrupada (objeto) con todos los campos de SessionData
export function getTotalsOfSessionObjectByCombined(session: any): SessionData {
  return {
    total_steps_automatic_forward: session.steps_automatic_forward ?? 0,
    total_steps_automatic_backward: session.steps_automatic_backward ?? 0,
    total_steps_intention_forward: session.steps_intention_forward ?? 0,
    total_steps_intention_backward: session.steps_intention_backward ?? 0,
    total_steps_aan1_forward: session.steps_aan1_forward ?? 0,
    total_steps_aan1_backward: session.steps_aan1_backward ?? 0,
    total_steps_aan2_forward: session.steps_aan2_forward ?? 0,
    total_steps_aan2_backward: session.steps_aan2_backward ?? 0,
    total_steps_automatic: 0,
    total_steps_intention: 0,
    total_steps_aan1: 0,
    total_steps_aan2: 0,
    total_steps_forward: 0,
    total_steps_backward: 0,
    total_steps_total: 0,
    total_time_automatic_forward: session.time_automatic_forward ?? 0,
    total_time_automatic_backward: session.time_automatic_backward ?? 0,
    total_time_intention_forward: session.time_intention_forward ?? 0,
    total_time_intention_backward: session.time_intention_backward ?? 0,
    total_time_aan1_forward: session.time_aan1_forward ?? 0,
    total_time_aan1_backward: session.time_aan1_backward ?? 0,
    total_time_aan2_forward: session.time_aan2_forward ?? 0,
    total_time_aan2_backward: session.time_aan2_backward ?? 0,
    total_time_automatic: 0,
    total_time_intention: 0,
    total_time_aan1: 0,
    total_time_aan2: 0,
    total_time_forward: 0,
    total_time_backward: 0,
    total_time_total: 0,
    total_time_use: 0,
    total_time_walking: 0,
    total_time_standing: 0,
    median_total_steps: 0,
    median_total_steps_automatic_forward: 0,
    median_total_steps_automatic_backward: 0,
    median_total_steps_intention_forward: 0,
    median_total_steps_intention_backward: 0,
    median_total_steps_automatic: 0,
    median_total_steps_intention: 0,
    median_total_steps_forward: 0,
    median_total_steps_backward: 0,
    median_total_time: 0,
    median_total_time_automatic_forward: 0,
    median_total_time_automatic_backward: 0,
    median_total_time_intention_forward: 0,
    median_total_time_intention_backward: 0,
    median_total_time_automatic: 0,
    median_total_time_intention: 0,
    median_total_time_forward: 0,
    median_total_time_backward: 0,
    evaluation: 0,
    cadence_automatic_forward: session.cadence_automatic_forward ?? 0,
    cadence_automatic_backward: session.cadence_automatic_backward ?? 0,
    cadence_intention_forward: session.cadence_intention_forward ?? 0,
    cadence_intention_backward: session.cadence_intention_backward ?? 0,
    flexos_hip: 0,
    flexos_knee: 0,
    flexos_ankle: 0,
    threshold_hipl: 0,
    threshold_hipr: 0,
    threshold_kneel: 0,
    threshold_kneer: 0,
    effort: 0,
    torque_hipl: 0,
    torque_hipr: 0,
    torque_kneel: 0,
    torque_kneer: 0,
    rom_hipl: 0,
    rom_hipr: 0,
    rom_kneel: 0,
    rom_kneer: 0,
    fc_max: 0,
    fc_med: 0,
    so2: 0,
  };
}

/**
 * Agrupa sesiones por fecha y devuelve array de arrays
 * Cada elemento es un array de sesiones de la misma fecha
 */
export function groupSessionsByDateArray(
  sessions: any[],
  timeZone?: string
): any[][] {
  const grouped: { [date: string]: any[] } = {};

  sessions.forEach((session) => {
    const rawDate = session.date || session.sessionDate;
    if (!rawDate) return;

    const date = sessionDateKey(rawDate, timeZone);
    if (!grouped[date]) grouped[date] = [];
    grouped[date].push(session);
  });

  return Object.values(grouped);
}

// Función nueva para gráfica combinada de pasos por fecha (agrupada por fecha)
export function getStepsCombinedGraphByDateSeries(
  sessions: any[],
  t: TFunction<'translation', undefined>,
  deviceModel: DeviceModelType | null,
  COLORS: any,
  timeZone?: string
) {
  const isExplorerClinico = deviceModel === DeviceModelType.EXPLORER_CLINICO;
  const isAtlas = deviceModel === DeviceModelType.ATLAS;

  // 1. ORDENAR SESIONES CRONOLÓGICAMENTE
  const sortedSessions = [...sessions].sort((a, b) => {
    const dateA = new Date(a.date || a.sessionDate).getTime();
    const dateB = new Date(b.date || b.sessionDate).getTime();
    return dateA - dateB;
  });

  let configMap;
  switch (deviceModel) {
    case DeviceModelType.ATLAS:
      configMap = [
        {
          dataKey: 'automatic_forward',
          tKey: 'automaticForward',
          color: COLORS.forwardAutomatic,
        },
        {
          dataKey: 'automatic_backward',
          tKey: 'automaticBackward',
          color: COLORS.backwardAutomatic,
        },
        {
          dataKey: 'intention_forward',
          tKey: 'activeModeForward',
          color: COLORS.forwardIntention,
        },
        {
          dataKey: 'intention_backward',
          tKey: 'activeModeBackward',
          color: COLORS.backwardIntention,
        },
      ];
      break;

    case DeviceModelType.EXPLORER_PERSONAL:
      configMap = [
        {
          dataKey: 'automatic_forward',
          tKey: 'automaticForward',
          color: COLORS.forwardAutomatic,
        },
        {
          dataKey: 'automatic_backward',
          tKey: 'automaticBackward',
          color: COLORS.backwardAutomatic,
        },
        {
          dataKey: 'intention_forward',
          tKey: 'forwardIntention',
          color: COLORS.forwardIntention,
        },
        {
          dataKey: 'intention_backward',
          tKey: 'backwardIntention',
          color: COLORS.backwardIntention,
        },
      ];
      break;

    case DeviceModelType.EXPLORER_CLINICO:
    case null:
      configMap = [
        {
          dataKey: 'automatic_forward',
          tKey: 'automaticForward',
          color: COLORS.forwardAutomatic,
        },
        {
          dataKey: 'automatic_backward',
          tKey: 'automaticBackward',
          color: COLORS.backwardAutomatic,
        },
        {
          dataKey: 'intention_forward',
          tKey: 'forwardIntention',
          color: COLORS.forwardIntention,
        },
        {
          dataKey: 'intention_backward',
          tKey: 'backwardIntention',
          color: COLORS.backwardIntention,
        },
        {
          dataKey: 'aan1_forward',
          tKey: 'ann1Forward',
          color: COLORS.forwardAnn1,
        },
        {
          dataKey: 'aan1_backward',
          tKey: 'ann1Backward',
          color: COLORS.backwardAnn1,
        },
        {
          dataKey: 'aan2_forward',
          tKey: 'ann2Forward',
          color: COLORS.forwardAnn2,
        },
        {
          dataKey: 'aan2_backward',
          tKey: 'ann2Backward',
          color: COLORS.backwardAnn2,
        },
      ];
      break;

    case undefined:
    default:
      configMap = [
        {
          dataKey: 'automatic_forward',
          tKey: 'automaticForward',
          color: COLORS.forwardAutomatic,
        },
        {
          dataKey: 'automatic_backward',
          tKey: 'automaticBackward',
          color: COLORS.backwardAutomatic,
        },
        {
          dataKey: 'intention_forward',
          tKey: 'forwardIntention',
          color: COLORS.forwardIntention,
        },
        {
          dataKey: 'intention_backward',
          tKey: 'backwardIntention',
          color: COLORS.backwardIntention,
        },
      ];
      break;
  }
  // Agrupa sesiones usando el array ya ordenado
  const sesionesPorFecha = groupSessionsByDateArray(sortedSessions, timeZone);

  const labels = sesionesPorFecha.map((grupo) => {
    const rawDate = grupo[0]?.date || grupo[0]?.sessionDate;
    if (!rawDate) return 'Sin fecha';

    return formatSessionDate(rawDate, timeZone, {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });
  });

  const datasets = configMap.map((item) => {
    const dataArray = sesionesPorFecha.map((grupo) => {
      const combined = grupo.reduce((acc: any, session: any) => {
        acc[`steps_automatic_forward`] =
          (acc[`steps_automatic_forward`] || 0) +
          (session.steps_automatic_forward || 0);

        acc[`steps_automatic_backward`] =
          (acc[`steps_automatic_backward`] || 0) +
          (session.steps_automatic_backward || 0);

        acc[`steps_intention_forward`] =
          (acc[`steps_intention_forward`] || 0) +
          (session.steps_intention_forward || 0);

        acc[`steps_intention_backward`] =
          (acc[`steps_intention_backward`] || 0) +
          (session.steps_intention_backward || 0);

        acc[`steps_aan1_forward`] =
          (acc[`steps_aan1_forward`] || 0) + (session.steps_aan1_forward || 0);

        acc[`steps_aan1_backward`] =
          (acc[`steps_aan1_backward`] || 0) +
          (session.steps_aan1_backward || 0);

        acc[`steps_aan2_forward`] =
          (acc[`steps_aan2_forward`] || 0) + (session.steps_aan2_forward || 0);

        acc[`steps_aan2_backward`] =
          (acc[`steps_aan2_backward`] || 0) +
          (session.steps_aan2_backward || 0);

        return acc;
      }, {});

      const totals = getTotalsOfSessionObjectByCombined(combined);
      const valor = (totals as any)[`total_steps_${item.dataKey}`];
      return valor ?? 0;
    });

    return {
      label: t(`pages.reports.graphs.${item.tKey}`),
      data: dataArray,
      fill: false, //fill: true activa el sombreado.
      backgroundColor: item.color + '80', // esto genera el sombreado
      borderColor: item.color,
      borderWidth: 2,
      pointRadius: 2,
      tension: 0.3,
    };
  });

  const chartData = { labels, datasets };

  const chartOptions: any = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        align: 'center' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12,
          },
        },
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          label: (context: any) => {
            const label = context.dataset.label || '';
            let value = context.parsed.y ?? context.parsed ?? 0;
            // Si es array, mostrar 0
            if (Array.isArray(value)) value = 0;
            // Si es objeto, intentar extraer .y o .x
            else if (typeof value === 'object' && value !== null) {
              value = value.y ?? value.x ?? 0;
              if (typeof value === 'object' || Array.isArray(value)) value = 0;
            }
            // Limitar a dos enteros y dos decimales
            value =
              typeof value === 'number'
                ? value.toLocaleString(undefined, {
                  minimumIntegerDigits: 2,
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
                : 0;
            return `${label}: ${value}`;
          },
        },
      },
      datalabels: {
        display: false,
      },
    },
    scales: {
      y: {
        grid: { display: true },
        stacked: true,
        beginAtZero: true,
        ticks: {
          precision: 0,
          font: { size: 12 },
        },
      },
      x: {
        grid: { display: false },
        stacked: true,
        ticks: {
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 8,
          font: { size: 10 },
        },
      },
    },
  };

  return { data: chartData, options: chartOptions };
}
// Grafica de tiempo por fecha (eje X = fechas, datasets = modo+dirección, eje Y = tiempo)
export function getTimeCombinedGraphByDateSeries(
  sessions: any[],
  t: TFunction<'translation', undefined>,
  deviceModel: DeviceModelType,
  COLORS: any,
  timeZone?: string
) {
  const isExplorerClinico = deviceModel === DeviceModelType.EXPLORER_CLINICO;
  const isAtlas = deviceModel === DeviceModelType.ATLAS;

  let configMap;
  switch (deviceModel) {
    case DeviceModelType.ATLAS:
      configMap = [
        {
          dataKey: 'automatic_forward',
          tKey: 'automaticForward',
          color: COLORS.forwardAutomatic,
        },
        {
          dataKey: 'automatic_backward',
          tKey: 'automaticBackward',
          color: COLORS.backwardAutomatic,
        },
        {
          dataKey: 'intention_forward',
          tKey: 'activeModeForward',
          color: COLORS.forwardIntention,
        },
        {
          dataKey: 'intention_backward',
          tKey: 'activeModeBackward',
          color: COLORS.backwardIntention,
        },
      ];
      break;

    case DeviceModelType.EXPLORER_PERSONAL:
      configMap = [
        {
          dataKey: 'automatic_forward',
          tKey: 'automaticForward',
          color: COLORS.forwardAutomatic,
        },
        {
          dataKey: 'automatic_backward',
          tKey: 'automaticBackward',
          color: COLORS.backwardAutomatic,
        },
        {
          dataKey: 'intention_forward',
          tKey: 'forwardIntention',
          color: COLORS.forwardIntention,
        },
        {
          dataKey: 'intention_backward',
          tKey: 'backwardIntention',
          color: COLORS.backwardIntention,
        },
      ];
      break;

    case DeviceModelType.EXPLORER_CLINICO:
    case null:
      configMap = [
        {
          dataKey: 'automatic_forward',
          tKey: 'automaticForward',
          color: COLORS.forwardAutomatic,
        },
        {
          dataKey: 'automatic_backward',
          tKey: 'automaticBackward',
          color: COLORS.backwardAutomatic,
        },
        {
          dataKey: 'intention_forward',
          tKey: 'forwardIntention',
          color: COLORS.forwardIntention,
        },
        {
          dataKey: 'intention_backward',
          tKey: 'backwardIntention',
          color: COLORS.backwardIntention,
        },
        {
          dataKey: 'aan1_forward',
          tKey: 'ann1Forward',
          color: COLORS.forwardAnn1,
        },
        {
          dataKey: 'aan1_backward',
          tKey: 'ann1Backward',
          color: COLORS.backwardAnn1,
        },
        {
          dataKey: 'aan2_forward',
          tKey: 'ann2Forward',
          color: COLORS.forwardAnn2,
        },
        {
          dataKey: 'aan2_backward',
          tKey: 'ann2Backward',
          color: COLORS.backwardAnn2,
        },
      ];
      break;

    case undefined:
    default:
      configMap = [
        {
          dataKey: 'automatic_forward',
          tKey: 'automaticForward',
          color: COLORS.forwardAutomatic,
        },
        {
          dataKey: 'automatic_backward',
          tKey: 'automaticBackward',
          color: COLORS.backwardAutomatic,
        },
        {
          dataKey: 'intention_forward',
          tKey: 'forwardIntention',
          color: COLORS.forwardIntention,
        },
        {
          dataKey: 'intention_backward',
          tKey: 'backwardIntention',
          color: COLORS.backwardIntention,
        },
      ];
      break;
  }

  // Agrupamos sesiones por fecha
  const sesionesPorFecha = groupSessionsByDateArray(sessions, timeZone).filter(
    (grupo) => grupo.length > 0
  );
  // Eje X: fechas
  const labels = sesionesPorFecha.map((grupo) => {
    const rawDate = grupo[0]?.date || grupo[0]?.sessionDate;
    if (!rawDate) return 'Sin fecha';
    const date = new Date(rawDate);
    return isNaN(date.getTime())
      ? 'Sin fecha'
      : formatSessionDate(rawDate, timeZone, {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      });
  });

  // Cada dataset es un modo+dirección
  const datasets = configMap.map((item) => {
    const dataArray = sesionesPorFecha.map((grupo) => {
      // Sumar los valores de todos los objetos del grupo para la clave correspondiente
      const combined = grupo.reduce((acc: any, session: any) => {
        acc[`time_automatic_forward`] =
          (acc[`time_automatic_forward`] || 0) +
          (session.time_automatic_forward || 0);
        acc[`time_automatic_backward`] =
          (acc[`time_automatic_backward`] || 0) +
          (session.time_automatic_backward || 0);
        acc[`time_intention_forward`] =
          (acc[`time_intention_forward`] || 0) +
          (session.time_intention_forward || 0);
        acc[`time_intention_backward`] =
          (acc[`time_intention_backward`] || 0) +
          (session.time_intention_backward || 0);
        acc[`time_aan1_forward`] =
          (acc[`time_aan1_forward`] || 0) + (session.time_aan1_forward || 0);
        acc[`time_aan1_backward`] =
          (acc[`time_aan1_backward`] || 0) + (session.time_aan1_backward || 0);
        acc[`time_aan2_forward`] =
          (acc[`time_aan2_forward`] || 0) + (session.time_aan2_forward || 0);
        acc[`time_aan2_backward`] =
          (acc[`time_aan2_backward`] || 0) + (session.time_aan2_backward || 0);
        return acc;
      }, {});
      const totals = getTotalsOfSessionObjectByCombined(combined);
      const valor = (totals as any)[`total_time_${item.dataKey}`];
      return valor ?? 0;
    });
    return {
      label: t(`pages.reports.graphs.${item.tKey}`),
      data: dataArray,
      fill: false, //fill: true activa el sombreado.
      backgroundColor: item.color + '80',
      borderColor: item.color,
      borderWidth: 2,
      pointRadius: 2,
      tension: 0.3,
    };
  });

  const chartData = { labels, datasets };

  const chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    aspectRatio: 1.1,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 10,
          font: { size: 12 },
        },
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const label = context.dataset.label || '';
            let value = context.parsed.y ?? context.parsed ?? 0;
            if (Array.isArray(value)) value = 0;
            else if (typeof value === 'object' && value !== null) {
              value = value.y ?? value.x ?? 0;
              if (typeof value === 'object' || Array.isArray(value)) value = 0;
            }
            value =
              typeof value === 'number'
                ? value.toLocaleString(undefined, {
                  minimumIntegerDigits: 2,
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
                : 0;
            return `${label}: ${value}`;
          },
        },
      },
      datalabels: {
        display: false,
        borderColor: 'white',
        borderRadius: 50,
        borderWidth: 2,
        padding: 3,
        font: {
          weight: 'bold' as const,
          size: 12,
        },
        backgroundColor: 'white',
        color: 'black',
        formatter: (value: any) => `${value}`,
      },
    },
    scales: {
      y: {
        grid: { display: true },
        beginAtZero: true,
        ticks: { precision: 0 },
      },
      x: {
        grid: { display: false },
        ticks: {
          autoSkip: false,
          maxRotation: 45,
          minRotation: 45,
          font: { size: 12 },
        },
      },
    },
  };

  return { data: chartData, options: chartOptions };
}

// Grafica de cadencia por fecha (eje X = fechas, datasets = modo+dirección, eje Y = cadencia)
export function getCadenceCombinedGraphByDateSeries(
  sessions: any[],
  t: TFunction<'translation', undefined>,
  deviceModel: DeviceModelType,
  COLORS: any,
  timeZone?: string
) {
  const isExplorerClinico = deviceModel === DeviceModelType.EXPLORER_CLINICO;
  const isAtlas = deviceModel === DeviceModelType.ATLAS;

  let configMap;
  switch (deviceModel) {
    case DeviceModelType.ATLAS:
      configMap = [
        {
          dataKey: 'automatic_forward',
          tKey: 'automaticForward',
          color: COLORS.forwardAutomatic,
        },
        {
          dataKey: 'automatic_backward',
          tKey: 'automaticBackward',
          color: COLORS.backwardAutomatic,
        },
        {
          dataKey: 'intention_forward',
          tKey: 'activeModeForward',
          color: COLORS.forwardIntention,
        },
        {
          dataKey: 'intention_backward',
          tKey: 'activeModeBackward',
          color: COLORS.backwardIntention,
        },
      ];
      break;

    case DeviceModelType.EXPLORER_PERSONAL:
      configMap = [
        {
          dataKey: 'automatic_forward',
          tKey: 'automaticForward',
          color: COLORS.forwardAutomatic,
        },
        {
          dataKey: 'automatic_backward',
          tKey: 'automaticBackward',
          color: COLORS.backwardAutomatic,
        },
        {
          dataKey: 'intention_forward',
          tKey: 'forwardIntention',
          color: COLORS.forwardIntention,
        },
        {
          dataKey: 'intention_backward',
          tKey: 'backwardIntention',
          color: COLORS.backwardIntention,
        },
      ];
      break;

    case DeviceModelType.EXPLORER_CLINICO:
    case null:
      configMap = [
        {
          dataKey: 'automatic_forward',
          tKey: 'automaticForward',
          color: COLORS.forwardAutomatic,
        },
        {
          dataKey: 'automatic_backward',
          tKey: 'automaticBackward',
          color: COLORS.backwardAutomatic,
        },
        {
          dataKey: 'intention_forward',
          tKey: 'forwardIntention',
          color: COLORS.forwardIntention,
        },
        {
          dataKey: 'intention_backward',
          tKey: 'backwardIntention',
          color: COLORS.backwardIntention,
        },
        {
          dataKey: 'aan1_forward',
          tKey: 'ann1Forward',
          color: COLORS.forwardAnn1,
        },
        {
          dataKey: 'aan1_backward',
          tKey: 'ann1Backward',
          color: COLORS.backwardAnn1,
        },
        {
          dataKey: 'aan2_forward',
          tKey: 'ann2Forward',
          color: COLORS.forwardAnn2,
        },
        {
          dataKey: 'aan2_backward',
          tKey: 'ann2Backward',
          color: COLORS.backwardAnn2,
        },
      ];
      break;

    case undefined:
    default:
      configMap = [
        {
          dataKey: 'automatic_forward',
          tKey: 'automaticForward',
          color: COLORS.forwardAutomatic,
        },
        {
          dataKey: 'automatic_backward',
          tKey: 'automaticBackward',
          color: COLORS.backwardAutomatic,
        },
        {
          dataKey: 'intention_forward',
          tKey: 'forwardIntention',
          color: COLORS.forwardIntention,
        },
        {
          dataKey: 'intention_backward',
          tKey: 'backwardIntention',
          color: COLORS.backwardIntention,
        },
      ];
      break;
  }

  // Agrupamos sesiones por fecha
  const sesionesPorFecha = groupSessionsByDateArray(sessions, timeZone).filter(
    (grupo) => grupo.length > 0
  );
  const labels = sesionesPorFecha.map((grupo) => {
    const rawDate = grupo[0]?.date || grupo[0]?.sessionDate;
    if (!rawDate) return 'Sin fecha';

    // Convertimos a objeto Date y formateamos
    return formatSessionDate(rawDate, timeZone, {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit', // O 'numeric' para 2026 completo
    });
  });
  // Cada dataset es un modo+dirección
  const datasets = configMap.map((item) => {
    const dataArray = sesionesPorFecha.map((grupo) => getCadence(grupo, [item.dataKey]));
    return {
      label: t(`pages.reports.graphs.${item.tKey}`),
      data: dataArray,
      fill: false, //fill: true activa el sombreado.
      backgroundColor: item.color + '80',
      borderColor: item.color,
      borderWidth: 2,
      pointRadius: 2,
      tension: 0.3,
    };

  });

  const chartData = { labels, datasets };

  const chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    aspectRatio: 1.1,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 10,
          font: { size: 12 },
        },
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const label = context.dataset.label || '';
            let value = context.parsed.y ?? context.parsed ?? 0;
            if (Array.isArray(value)) value = 0;
            else if (typeof value === 'object' && value !== null) {
              value = value.y ?? value.x ?? 0;
              if (typeof value === 'object' || Array.isArray(value)) value = 0;
            }
            value =
              typeof value === 'number'
                ? value.toLocaleString(undefined, {
                  minimumIntegerDigits: 2,
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
                : 0;
            return `${label}: ${value}`;
          },
        },
      },
      datalabels: {
        display: false,
        borderColor: 'white',
        borderRadius: 50,
        borderWidth: 2,
        padding: 3,
        font: {
          weight: 'bold' as const,
          size: 12,
        },
        backgroundColor: 'white',
        color: 'black',
        formatter: (value: any) => `${value}`,
      },
    },
    scales: {
      y: {
        grid: { display: true },
        beginAtZero: true,
        ticks: { precision: 0 },
      },
      x: {
        grid: { display: false },
        ticks: {
          autoSkip: false,
          maxRotation: 45,
          minRotation: 45,
          font: { size: 12 },
        },
      },
    },
  };

  return { data: chartData, options: chartOptions };
}

/**
 * Get sessions grouped by month - REPORT
 *
 * @param {any[]} sessions - List of sessions
 * @return {any[]} - Grouped sessions by month
 */
export function getSessionsByMonth(sessions: any[], timeZone?: string) {
  const sessions_by_month: {
    sessions: sessions[];
    month: number | string;
    month_string: Date;
  }[] = [];
  sessions.forEach((session) => {
    const date = new Date(session.date);
    const month = timeZone
      ? sessionDateKey(session.date, timeZone).slice(0, 7)
      : date.getMonth();
    const month_string = date;
    const session_by_month = sessions_by_month.find(
      (session_by_month) => session_by_month.month === month
    );
    if (session_by_month) {
      session_by_month.sessions.push(session);
    } else {
      sessions_by_month.push({
        sessions: [session],
        month,
        month_string,
      });
    }
  });

  // Sort by month_string
  sessions_by_month.sort((a, b) => {
    return (
      new Date(a.month_string).getTime() - new Date(b.month_string).getTime()
    );
  });

  return sessions_by_month;
}

//==============FUNCTION CONFIGURATION GRAPHICS=================//

// Distribution of steps - CIRCULAR
//Uso del dispositivo (Check de Pasos)
export function getStepGraph(
  data: SessionData,
  documentStyle: CSSStyleDeclaration,
  t: TFunction<'translation', undefined>,
  window: any,
  legend: boolean,
  inform?: boolean,
  selectedModel?: string | number | null
) {
  type StepEntry = {
    label: string;
    steps: number;
    backgroundColor: string;
    hoverBackgroundColor: string;
    borderColor: string;
  };

  const baseEntries = {
    automaticForward: {
      label: t('pages.reports.graphs.automaticForward'),
      steps: data.total_steps_automatic_forward ?? 0,
      backgroundColor: getTransparentColor(
        documentStyle.getPropertyValue('--forward-automatic-color-1'),
        0.1
      ),
      hoverBackgroundColor: documentStyle.getPropertyValue(
        '--forward-automatic-color-2'
      ),
      borderColor: documentStyle.getPropertyValue(
        '--forward-automatic-color-1'
      ),
    },
    automaticBackward: {
      label: t('pages.reports.graphs.automaticBackward'),
      steps: data.total_steps_automatic_backward ?? 0,
      backgroundColor: getTransparentColor(
        documentStyle.getPropertyValue('--backward-automatic-color-1'),
        0.1
      ),
      hoverBackgroundColor: documentStyle.getPropertyValue(
        '--backward-automatic-color-2'
      ),
      borderColor: documentStyle.getPropertyValue(
        '--backward-automatic-color-1'
      ),
    },
    intentionForward: {
      label: t('pages.reports.graphs.forwardIntention'),
      steps: data.total_steps_intention_forward ?? 0,
      backgroundColor: getTransparentColor(
        documentStyle.getPropertyValue('--forward-intention-color-1'),
        0.1
      ),
      hoverBackgroundColor: documentStyle.getPropertyValue(
        '--forward-intention-color-2'
      ),
      borderColor: documentStyle.getPropertyValue(
        '--forward-intention-color-1'
      ),
    },
    intentionBackward: {
      label: t('pages.reports.graphs.backwardIntention'),
      steps: data.total_steps_intention_backward ?? 0,
      backgroundColor: getTransparentColor(
        documentStyle.getPropertyValue('--backward-intention-color-1'),
        0.1
      ),
      hoverBackgroundColor: documentStyle.getPropertyValue(
        '--backward-intention-color-2'
      ),
      borderColor: documentStyle.getPropertyValue(
        '--backward-intention-color-1'
      ),
    },
    aan1Forward: {
      label: t('pages.reports.graphs.ann1Forward'),
      steps: data.total_steps_aan1_forward ?? 0,
      backgroundColor: getTransparentColor(
        documentStyle.getPropertyValue('--ann1-forward-color-1'),
        0.1
      ),
      hoverBackgroundColor: documentStyle.getPropertyValue(
        '--ann1-forward-color-2'
      ),
      borderColor: documentStyle.getPropertyValue('--ann1-forward-color-1'),
    },
    aan1Backward: {
      label: t('pages.reports.graphs.ann1Backward'),
      steps: data.total_steps_aan1_backward ?? 0,
      backgroundColor: getTransparentColor(
        documentStyle.getPropertyValue('--ann1-backward-color-1'),
        0.1
      ),
      hoverBackgroundColor: documentStyle.getPropertyValue(
        '--ann1-backward-color-2'
      ),
      borderColor: documentStyle.getPropertyValue('--ann1-backward-color-1'),
    },
    aan2Forward: {
      label: t('pages.reports.graphs.ann2Forward'),
      steps: data.total_steps_aan2_forward ?? 0,
      backgroundColor: getTransparentColor(
        documentStyle.getPropertyValue('--ann2-forward-color-1'),
        0.1
      ),
      hoverBackgroundColor: documentStyle.getPropertyValue(
        '--ann2-forward-color-2'
      ),
      borderColor: documentStyle.getPropertyValue('--ann2-forward-color-1'),
    },
    aan2Backward: {
      label: t('pages.reports.graphs.ann2Backward'),
      steps: data.total_steps_aan2_backward ?? 0,
      backgroundColor: getTransparentColor(
        documentStyle.getPropertyValue('--ann2-backward-color-1'),
        0.1
      ),
      hoverBackgroundColor: documentStyle.getPropertyValue(
        '--ann2-backward-color-2'
      ),
      borderColor: documentStyle.getPropertyValue('--ann2-backward-color-1'),
    },
  };

  const getEntriesByModel = (): StepEntry[] => {
    switch (selectedModel) {
      case DeviceModelType.ATLAS:
        return [
          baseEntries.automaticForward,
          baseEntries.automaticBackward,
          {
            ...baseEntries.intentionForward,
            label: t('pages.reports.graphs.activeModeForward'),
          },
          {
            ...baseEntries.intentionBackward,
            label: t('pages.reports.graphs.activeModeBackward'),
          },
        ];

      case DeviceModelType.EXPLORER_PERSONAL:
        return [
          baseEntries.automaticForward,
          baseEntries.automaticBackward,
          baseEntries.intentionForward,
          baseEntries.intentionBackward,
        ];

      case DeviceModelType.EXPLORER_CLINICO:
      case null:
      case undefined:
      default:
        return [
          baseEntries.automaticForward,
          baseEntries.automaticBackward,
          baseEntries.intentionForward,
          baseEntries.intentionBackward,
          baseEntries.aan1Forward,
          baseEntries.aan1Backward,
          baseEntries.aan2Forward,
          baseEntries.aan2Backward,
        ];
    }
  };

  const visibleEntries = getEntriesByModel();

  // El total debe depender solo de los modos visibles del modelo seleccionado
  const visibleTotalSteps = visibleEntries.reduce(
    (acc, entry) => acc + entry.steps,
    0
  );

  const dataStepsDistribution: ChartData = {
    labels: visibleEntries.map((entry) => entry.label),
    datasets: [
      {
        data: visibleEntries.map((entry) =>
          get_percentage(entry.steps, visibleTotalSteps)
        ),
        backgroundColor: visibleEntries.map((entry) => entry.backgroundColor),
        hoverBackgroundColor: visibleEntries.map(
          (entry) => entry.hoverBackgroundColor
        ),
        borderColor: visibleEntries.map((entry) => entry.borderColor),
      },
    ],
  };

  const optionsStepsDistribution: ChartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    aspectRatio: 1.1,
    plugins: {
      legend: {
        display: legend,
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 10,
          font: {
            size: 12,
          },
        },
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const percentValue = formatNumber(
              dataStepsDistribution.datasets[0].data[context.dataIndex],
              i18next.language
            );

            const steps = visibleEntries[context.dataIndex]?.steps ?? 0;
            const formattedSteps = formatNumber(steps, i18next.language);

            return `${percentValue}% (${formattedSteps} ${t('steps')})`;
          },
        },
      },
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      datalabels: {
        display: false,
        borderColor: 'white',
        borderRadius: 50,
        borderWidth: 2,
        padding: 3,
        font: {
          weight: 'bold',
          size: inform ? 16 : 12,
        },
        backgroundColor: 'white',
        color: 'black',
        formatter: (value: any) => {
          const formattedValue = formatNumber(value, i18next.language);
          return `${formattedValue}%`;
        },
      },
    },
  };

  const optionsStepsDistributionInform: ChartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    aspectRatio: 1.1,
    plugins: {
      title: {
        display: true,
        text: t('pages.reports.graphs.percentageAnalysis'),
        font: {
          size: inform ? 20 : 16,
        },
      },
      legend: {
        display: legend,
        position: 'top',
        labels: {
          pointStyle: 'circle',
          font: {
            size: 20,
          },
        },
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const percentValue = formatNumber(
              dataStepsDistribution.datasets[0].data[context.dataIndex],
              i18next.language
            );

            const steps = visibleEntries[context.dataIndex]?.steps ?? 0;
            const formattedSteps = formatNumber(steps, i18next.language);

            return `${percentValue}% (${formattedSteps} pasos)`;
          },
        },
      },
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      datalabels: {
        display: showDisplayDataLabels(window?.innerWidth),
        borderColor: 'white',
        borderRadius: 50,
        borderWidth: 2,
        padding: 3,
        font: {
          weight: 'bold',
          size: 20,
        },
        backgroundColor: () => 'white',
        color: 'black',
        formatter: (value: any) => {
          const formattedValue = formatNumber(value, i18next.language);
          return `${formattedValue}%`;
        },
      },
    },
    scales: {
      x: {
        ticks: {
          font: {
            size: 20,
          },
        },
      },
      y: {
        ticks: {
          font: {
            size: 20,
          },
        },
        title: {
          display: true,
          text: t('pages.reports.graphs.percentage'),
          font: {
            size: 20,
          },
        },
      },
    },
  };

  return {
    dataStepsDistribution,
    optionsStepsDistribution: inform
      ? optionsStepsDistributionInform
      : optionsStepsDistribution,
  };
}
// Distribution of time - CIRCULAR
//uso del dispositivo (chek de Tiempo)
export function getStepGraphTime(
  data: SessionData,
  documentStyle: CSSStyleDeclaration,
  t: TFunction<'translation', undefined>,
  _window: any,
  selectedModel?: string | number | null
) {
  type TimeEntry = {
    label: string;
    time: number;
    backgroundColor: string;
    hoverBackgroundColor: string;
    borderColor: string;
  };
  const baseEntries = {
    automaticForward: {
      label: t('pages.reports.graphs.automaticForward'),
      time: data.total_time_automatic_forward ?? 0,
      backgroundColor: getTransparentColor(
        documentStyle.getPropertyValue('--forward-automatic-color-1'),
        0.1
      ),
      hoverBackgroundColor: documentStyle.getPropertyValue(
        '--forward-automatic-color-2'
      ),
      borderColor: documentStyle.getPropertyValue(
        '--forward-automatic-color-1'
      ),
    },
    automaticBackward: {
      label: t('pages.reports.graphs.automaticBackward'),
      time: data.total_time_automatic_backward ?? 0,
      backgroundColor: getTransparentColor(
        documentStyle.getPropertyValue('--backward-automatic-color-1'),
        0.1
      ),
      hoverBackgroundColor: documentStyle.getPropertyValue(
        '--backward-automatic-color-2'
      ),
      borderColor: documentStyle.getPropertyValue(
        '--backward-automatic-color-1'
      ),
    },
    intentionForward: {
      label: t('pages.reports.graphs.forwardIntention'),
      time: data.total_time_intention_forward ?? 0,
      backgroundColor: getTransparentColor(
        documentStyle.getPropertyValue('--forward-intention-color-1'),
        0.1
      ),
      hoverBackgroundColor: documentStyle.getPropertyValue(
        '--forward-intention-color-2'
      ),
      borderColor: documentStyle.getPropertyValue(
        '--forward-intention-color-1'
      ),
    },
    intentionBackward: {
      label: t('pages.reports.graphs.backwardIntention'),
      time: data.total_time_intention_backward ?? 0,
      backgroundColor: getTransparentColor(
        documentStyle.getPropertyValue('--backward-intention-color-1'),
        0.1
      ),
      hoverBackgroundColor: documentStyle.getPropertyValue(
        '--backward-intention-color-2'
      ),
      borderColor: documentStyle.getPropertyValue(
        '--backward-intention-color-1'
      ),
    },
    aan1Forward: {
      label: t('pages.reports.graphs.ann1Forward'),
      time: data.total_time_aan1_forward ?? 0,
      backgroundColor: getTransparentColor(
        documentStyle.getPropertyValue('--ann1-forward-color-1'),
        0.1
      ),
      hoverBackgroundColor: documentStyle.getPropertyValue(
        '--ann1-forward-color-2'
      ),
      borderColor: documentStyle.getPropertyValue('--ann1-forward-color-1'),
    },
    aan1Backward: {
      label: t('pages.reports.graphs.ann1Backward'),
      time: data.total_time_aan1_backward ?? 0,
      backgroundColor: getTransparentColor(
        documentStyle.getPropertyValue('--ann1-backward-color-1'),
        0.1
      ),
      hoverBackgroundColor: documentStyle.getPropertyValue(
        '--ann1-backward-color-2'
      ),
      borderColor: documentStyle.getPropertyValue('--ann1-backward-color-1'),
    },
    aan2Forward: {
      label: t('pages.reports.graphs.ann2Forward'),
      time: data.total_time_aan2_forward ?? 0,
      backgroundColor: getTransparentColor(
        documentStyle.getPropertyValue('--ann2-forward-color-1'),
        0.1
      ),
      hoverBackgroundColor: documentStyle.getPropertyValue(
        '--ann2-forward-color-2'
      ),
      borderColor: documentStyle.getPropertyValue('--ann2-forward-color-1'),
    },
    aan2Backward: {
      label: t('pages.reports.graphs.ann2Backward'),
      time: data.total_time_aan2_backward ?? 0,
      backgroundColor: getTransparentColor(
        documentStyle.getPropertyValue('--ann2-backward-color-1'),
        0.1
      ),
      hoverBackgroundColor: documentStyle.getPropertyValue(
        '--ann2-backward-color-2'
      ),
      borderColor: documentStyle.getPropertyValue('--ann2-backward-color-1'),
    },
  };
  const getEntriesByModel = (): TimeEntry[] => {
    switch (selectedModel) {
      case DeviceModelType.ATLAS:
        return [
          baseEntries.automaticForward,
          baseEntries.automaticBackward,
          {
            ...baseEntries.intentionForward,
            label: t('pages.reports.graphs.activeModeForward'),
          },
          {
            ...baseEntries.intentionBackward,
            label: t('pages.reports.graphs.activeModeBackward'),
          },
        ];

      case DeviceModelType.EXPLORER_PERSONAL:
        return [
          baseEntries.automaticForward,
          baseEntries.automaticBackward,
          baseEntries.intentionForward,
          baseEntries.intentionBackward,
        ];

      case DeviceModelType.EXPLORER_CLINICO:
      case null:
      case undefined:
      default:
        return [
          baseEntries.automaticForward,
          baseEntries.automaticBackward,
          baseEntries.intentionForward,
          baseEntries.intentionBackward,
          baseEntries.aan1Forward,
          baseEntries.aan1Backward,
          baseEntries.aan2Forward,
          baseEntries.aan2Backward,
        ];
    }
  };

  const visibleEntries = getEntriesByModel();
  const visibleTotalTime = visibleEntries.reduce(
    (acc, entry) => acc + entry.time,
    0
  );

  const dataStepsDistribution: ChartData = {
    labels: visibleEntries.map((entry) => entry.label),
    datasets: [
      {
        data: visibleEntries.map((entry) =>
          get_percentage(entry.time, visibleTotalTime)
        ),
        backgroundColor: visibleEntries.map((entry) => entry.backgroundColor),
        hoverBackgroundColor: visibleEntries.map(
          (entry) => entry.hoverBackgroundColor
        ),
        borderColor: visibleEntries.map((entry) => entry.borderColor),
      },
    ],
  };

  const optionsStepsDistribution: ChartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    aspectRatio: 1.1,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 10,
        },
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const value =
              dataStepsDistribution.datasets[0].data[context.dataIndex];
            const formattedValue = formatNumber(value, i18next.language);
            return `${formattedValue}%`;
          },
        },
      },
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      datalabels: {
        display: false, //showDisplayDataLabels(window?.innerWidth),
        borderColor: 'white',
        borderRadius: 50,
        borderWidth: 2,
        padding: 3,
        font: {
          weight: 'bold',
          size: 20,
        },
        backgroundColor: () => 'white',
        color: 'black',
        formatter: (value: any) => {
          const formattedValue = formatNumber(value, i18next.language);
          return `${formattedValue}%`;
        },
      },
    },
  };

  return {
    dataStepsDistribution,
    optionsStepsDistribution,
  };
}

// Torque graph - LINEAR
// Activation force graph - LINEAR
// Cada elemento de `data` es UN DÍA (el backend agrupa en processSessions)
// threshold_* = umbral máximo del día por articulación
export function getTorqueGraph(
  data: any,
  documentStyle: CSSStyleDeclaration,
  t: TFunction<'translation', undefined>,
  _deviceModel?: DeviceModelType // ya no se usa: el umbral no depende del dispositivo
) {
  // Copia para no modificar el array original al ordenar
  const _sessions: sessions[] = [...(data ?? [])].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  const labels = _sessions.map((s) =>
    new Date(s.date).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
    })
  );

  const toNumber = (value: any): number => {
    if (value === null || value === undefined) return 0;
    const n =
      typeof value === 'object' && 'toNumber' in value
        ? value.toNumber()
        : Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  // Solo mostramos el umbral si ese día hubo pasos en intención
  const usedIntention = (s: sessions) =>
    toNumber(s.steps_intention_forward) + toNumber(s.steps_intention_backward) >
    0;

  // El umbral ya llega en escala 1-10: no se convierte.
  // El primer punto usa 0 como ancla visual si aún no hubo intención.
  // En el resto de días sin intención, null evita inventar un umbral.
  const toThreshold = (
    s: sessions,
    value: any,
    isFirstSession: boolean
  ): number | null => {
    if (!usedIntention(s)) return isFirstSession ? 0 : null;
    const n = Math.abs(toNumber(value));
    return n;
  };

  const thresholdHipl = _sessions.map((s, index) =>
    toThreshold(s, s.threshold_hipl, index === 0)
  );
  const thresholdKneel = _sessions.map((s, index) =>
    toThreshold(s, s.threshold_kneel, index === 0)
  );
  const thresholdHipr = _sessions.map((s, index) =>
    toThreshold(s, s.threshold_hipr, index === 0)
  );
  const thresholdKneer = _sessions.map((s, index) =>
    toThreshold(s, s.threshold_kneer, index === 0)
  );

  const hasData = [
    thresholdHipl,
    thresholdKneel,
    thresholdHipr,
    thresholdKneer,
  ].some((serie) => serie.some((v) => v !== null));

  const baseDataset = {
    tension: 0.4,
    pointStyle: 'circle',
    pointRadius: 4, // que se vea cada día con dato
    pointHoverRadius: 7,
    spanGaps: true,
  };
  const dataTorque: ChartData = {
    labels,
    datasets: [
      {
        ...baseDataset,
        label: t('pages.patients.viewPatient.sessions.ci'),
        customKey: t('pages.patients.viewPatient.sessions.ci_expanded'),
        data: thresholdHipl,
        borderColor: documentStyle.getPropertyValue(
          '--forward-automatic-color-1'
        ),
        backgroundColor: 'rgba(0, 123, 255, 0.1)',
      },
      {
        ...baseDataset,
        label: t('pages.patients.viewPatient.sessions.ri'),
        customKey: t('pages.patients.viewPatient.sessions.ri_expanded'),
        data: thresholdKneel,
        borderColor: documentStyle.getPropertyValue(
          '--backward-automatic-color-1'
        ),
        backgroundColor: 'rgba(255, 193, 7, 0.1)',
      },
      {
        ...baseDataset,
        label: t('pages.patients.viewPatient.sessions.cd'),
        customKey: t('pages.patients.viewPatient.sessions.cd_expanded'),
        data: thresholdHipr,
        borderColor: documentStyle.getPropertyValue(
          '--backward-intention-color-1'
        ),
        backgroundColor: 'rgba(220, 53, 69, 0.1)',
      },
      {
        ...baseDataset,
        label: t('pages.patients.viewPatient.sessions.rd'),
        customKey: t('pages.patients.viewPatient.sessions.rd_expanded'),
        data: thresholdKneer,
        borderColor: documentStyle.getPropertyValue(
          '--forward-intention-color-1'
        ),
        backgroundColor: 'rgba(40, 167, 69, 0.1)',
      },
    ] as any,
  };

  const optionsTorque: ChartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    aspectRatio: 1,
    plugins: {
      legend: {
        position: 'top',
        labels: { usePointStyle: true },
      },
      // @ts-ignore
      datalabels: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const label = context.dataset.customKey ?? '';
            return `${label}: ${context.raw} ${t('pages.patients.viewPatient.threshold_force')}`;
          },
        },
      },
    },
    scales: {
      x: {
        display: true,
        grid: { display: false },
        ticks: { autoSkip: true, maxRotation: 45 },
      },
      y: {
        display: true,
        min: 0,
        max: 11, // margen visual por encima de 10
        grid: { color: 'rgba(200, 200, 200, 0.2)' },
        ticks: { stepSize: 2 },
        // quita la marca y la línea de 11: el eje solo muestra 0-10
        afterBuildTicks: (scale: any) => {
          scale.ticks = scale.ticks.filter((tick: any) => tick.value <= 10);
        },
        title: {
          display: true,
          text: t('pages.patients.viewPatient.threshold_force'),
          font: { size: 12, weight: 'bold' },
        },
      },
    },
  };

  return {
    dataTorque,
    optionsTorque,
    hasData,
  };
}

export function getScoreGraph(
  data: any,
  documentStyle: CSSStyleDeclaration,
  t: TFunction<'translation', undefined>,
  _window: any
) {
  const _sessions: sessions[] = data;

  _sessions.sort((a, b) => {
    return new Date(a.date).getTime() - new Date(b.date).getTime();
  });
  const labels = _sessions.map((s: any) =>
    new Date(s.date).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
    })
  );

  const dataScore: ChartData = {
    labels,
    datasets: [
      {
        label: t('pages.patients.viewPatient.sessions.score'),
        data: data.map((s: any) => s.evaluation),
        borderColor: '#004E78',
        backgroundColor: 'rgba(0, 123, 255, 0.1)',
        tension: 0.4,
        pointStyle: 'circle',
        pointRadius: 1.2,
        pointHoverRadius: 7,
      },
    ],
  };

  // Para que la tabla sea dinamica y si viene un valor mayor de 100% lo pinte
  const maxDataValue = Math.max(...data.map((s: any) => s.evaluation));
  const maxScale = Math.max(100, Math.ceil(maxDataValue / 10) * 10);

  const optionsScore: ChartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    aspectRatio: 1,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const value = dataScore.datasets[0].data[context.dataIndex];
            return `${t(
              'pages.patients.viewPatient.sessions.score'
            )}: ${value}`;
          },
        },
      },
      // @ts-ignore
      datalabels: {
        display: false, // Desactiva las etiquetas sobre los puntos
      },
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: false,
        },
        ticks: {
          autoSkip: true,
          maxRotation: 45,
        },
      },
      y: {
        display: true,
        max: maxScale,
        beginAtZero: true,
        ticks: {
          maxTicksLimit: 8,
          stepSize: 20,
        },
        title: {
          display: true,
          text: t('pages.patients.viewPatient.sessions.points'),
          font: {
            size: 12, // Tamaño de la fuente de la etiqueta
            weight: 'bold', // Peso de la fuente de la etiqueta
          },
        },
      },
    },
  };

  return {
    dataScore,
    optionsScore,
  };
}

// FC graph - LINEAR
export function getFCGraph(
  data: any,
  t: TFunction<'translation', undefined>,
  birth_date: string
) {
  const _sessions: sessions[] = data;

  _sessions.sort((a, b) => {
    return new Date(a.date).getTime() - new Date(b.date).getTime();
  });

  const labels = _sessions.map((s) =>
    new Date(s.date).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
    })
  );

  let threshold;

  if (!birth_date) {
    threshold = 208; // Valor por defecto si no se proporciona birth_date
  } else {
    const age = calculateAge(birth_date);
    threshold = Math.ceil(208 - 0.7 * age);
  }

  const dataFc: ChartData = {
    labels,
    datasets: [
      {
        label: t('pages.patients.viewPatient.sessions.fc_max'),
        data: data.map((s: any) => s.fc_max),
        borderColor: data.map((s: any) =>
          s.fc_max > threshold ? 'red' : '#004E78'
        ),
        backgroundColor: 'rgba(0, 123, 255, 0.1)',
        tension: 0.4,
        pointStyle: 'circle',
        pointRadius: data.map((s: any) => (s.fc_max > threshold ? 2.2 : 1.2)),
        pointHoverRadius: 7,
        pointBackgroundColor: data.map((s: any) =>
          s.fc_max > threshold ? 'red' : '#004E78'
        ),
        pointBorderColor: data.map((s: any) =>
          s.fc_max > threshold ? 'red' : '#004E78'
        ),
      },
      {
        label: t('pages.patients.viewPatient.sessions.fc_med'),
        data: data.map((s: any) => s.fc_med),
        borderColor: '#13DFB6',
        backgroundColor: 'rgba(0, 123, 255, 0.1)',
        tension: 0.4,
        pointStyle: 'circle',
        pointRadius: 1.2,
        pointHoverRadius: 7,
      },

      {
        label: t('pages.patients.viewPatient.sessions.threshold'),
        data: Array(labels.length).fill(threshold),
        borderColor: 'lightcoral',
        borderDash: [6, 6],
        pointRadius: 0,
        fill: false,
      },
    ],
  };

  //const maxDataValue = Math.max(...data.map((s: any) => s.fc_max));
  //const maxScale = Math.max(threshold, Math.ceil(maxDataValue / 10) * 10) + 10;

  const optionsFc: ChartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    aspectRatio: 1,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          color: '#000', // o el color que uses
        },
      },

      tooltip: {
        callbacks: {
          label: (context: any) => {
            const index = context.datasetIndex;
            const value = context.raw;

            if (index === 0) {
              // Línea de fc_max
              return `${t(
                'pages.patients.viewPatient.sessions.fc_max'
              )}: ${value}`;
            } else if (index === 1) {
              // Línea de fc_med
              return `${t(
                'pages.patients.viewPatient.sessions.fc_med'
              )}: ${value}`;
            } else {
              return `Valor: ${value}`;
            }
          },
        },
      },
      // @ts-ignore
      datalabels: {
        display: false, // Desactiva las etiquetas sobre los puntos
      },
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: false,
        },
        ticks: {
          autoSkip: true,
          maxRotation: 45,
        },
      },
      y: {
        display: true,
        beginAtZero: true,
        grid: {
          color: 'rgba(200, 200, 200, 0.2)',
        },
        ticks: { maxTicksLimit: 8, stepSize: 2 },
        title: {
          display: true,
          text: t('pages.patients.viewPatient.sessions.bpm'),
          font: {
            size: 12,
            weight: 'bold',
          },
        },
      },
    },
  };

  return {
    dataFc,
    optionsFc,
  };
}

// SO2 graph - LINEAR
export function getSO2Graph(data: any, t: TFunction<'translation', undefined>) {
  const _sessions: sessions[] = data;

  _sessions.sort((a, b) => {
    return new Date(a.date).getTime() - new Date(b.date).getTime();
  });
  const labels = _sessions.map((s) =>
    new Date(s.date).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
    })
  );

  const dataSO2: ChartData = {
    labels,
    datasets: [
      {
        label: t('pages.patients.viewPatient.sessions.so2'),
        data: data.map((s: any) => s.so2),
        borderColor: '#004E78',
        backgroundColor: 'rgba(0, 123, 255, 0.1)',
        tension: 0.4,
        pointStyle: 'circle',
        pointRadius: 1.2,
        pointHoverRadius: 7,
      },
    ],
  };

  const optionsSO2: ChartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    aspectRatio: 1,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const value = dataSO2.datasets[0].data[context.dataIndex];
            return `${t('pages.patients.viewPatient.sessions.so2')}: ${value}`;
          },
        },
      },
      // @ts-ignore
      datalabels: {
        display: false, // Desactiva las etiquetas sobre los puntos
      },
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: false,
        },
        ticks: {
          autoSkip: true,
          maxRotation: 45,
        },
      },
      y: {
        display: true,
        max: 100,
        beginAtZero: true,
        ticks: {
          maxTicksLimit: 8,
          stepSize: 20,
        },
        title: {
          display: true,
          text: t('pages.reports.graphs.percentage'),
          font: {
            size: 12, // Tamaño de la fuente de la etiqueta
            weight: 'bold', // Peso de la fuente de la etiqueta
          },
        },
      },
    },
  };

  return {
    dataSO2,
    optionsSO2,
  };
}

// Steps graph = DIRECTION - LINEAR
export function getStepsDirectionGraph(
  data: any,
  documentStyle: CSSStyleDeclaration,
  t: TFunction<'translation', undefined>,
  textColorSecondary: string,
  textColor: string,
  surfaceBorder: string
) {
  const _sessions: sessions[] = data;

  _sessions.sort((a, b) => {
    return new Date(a.date).getTime() - new Date(b.date).getTime();
  });
  const labels = _sessions.map((s) =>
    new Date(s.date).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
    })
  );
  // Direction
  const direction_forward = _sessions.map(
    (s) => s.steps_automatic_forward + s.steps_intention_forward
  );
  const direction_backward = _sessions.map(
    (s) => s.steps_automatic_backward + s.steps_intention_backward
  );

  const dataStepsDirection: ChartData = {
    labels,
    datasets: [
      {
        label: t('forward'),
        data: direction_forward,
        backgroundColor: 'rgba(0, 78, 120, 0.1)', // Color del área
        borderColor: '#004E78',

        tension: 0.4, // Suavizar la línea
        pointStyle: 'circle', // Estilo de los puntos
        pointRadius: 1.2,
        pointHoverRadius: 7,
      },
      {
        label: t('backward'),
        data: direction_backward,
        backgroundColor: 'rgba(19, 223, 182, 0.1)',
        borderColor: '#13DFB6',
        tension: 0.4,
        pointStyle: 'circle',
        pointRadius: 1.2,
        pointHoverRadius: 7,
      },
    ],
  };

  const optionsStepsDirection: ChartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    aspectRatio: 1,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          color: textColor,
          padding: 20,
        },
      },
      // @ts-ignore
      datalabels: {
        display: false,
      },
    },

    scales: {
      x: {
        ticks: {
          color: textColorSecondary,
          autoSkip: true,
          maxRotation: 45,
        },
        grid: {
          display: false,
          color: surfaceBorder,
        },
      },
      y: {
        ticks: {
          color: textColorSecondary,
          maxTicksLimit: 8,
          stepSize: 100,
        },
        grid: {
          color: surfaceBorder,
        },
        title: {
          display: true,
          text: t('pages.patients.viewPatient.sessions.amount'),
          font: {
            size: 14,
            weight: 'bold',
          },
        },
      },
    },
  };

  return {
    dataStepsDirection,
    optionsStepsDirection,
  };
}

export function getDataStepsMode(
  data: any,
  role: Role,
  documentStyle: CSSStyleDeclaration,
  t: TFunction<'translation', undefined>,
  textColorSecondary: string,
  textColor: string,
  surfaceBorder: string,
  deviceModel?: DeviceModelType
) {
  const _sessions: sessions[] = data;
  const canViewAssistanceModes = can(
    role,
    AvailableResourceActions.visualization.view_assistance_modes_graphs
  );

  _sessions.sort((a, b) => {
    return new Date(a.date).getTime() - new Date(b.date).getTime();
  });
  const labels = _sessions.map((s) =>
    new Date(s.date).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
    })
  );

  // Mode automatic
  const mode_automatic = _sessions.map(
    (s) => s.steps_automatic_backward + s.steps_automatic_forward
  );

  // Mode intention
  const mode_intention = _sessions.map(
    (s) => s.steps_intention_backward + s.steps_intention_forward
  );
  // Filtrado de modos según el modelo de dispositivo
  const isAtlas = deviceModel === 5;
  const isExplorerPersonal = deviceModel === 6;
  let mode_aan1: number[] = [];
  let mode_aan2: number[] = [];
  if (canViewAssistanceModes && !isAtlas && !isExplorerPersonal) {
    // Mode aan1
    mode_aan1 = _sessions.map(
      (s) => (s.steps_aan1_backward ?? 0) + (s.steps_aan1_forward ?? 0)
    );

    // Mode aan2
    mode_aan2 = _sessions.map(
      (s) => (s.steps_aan2_backward ?? 0) + (s.steps_aan2_forward ?? 0)
    );
  }

  let datasets: ChartDataset[] = [];
  // Si es Atlas, solo mostrar Automático y Modo activo (no intención ni asistencias)
  if (deviceModel === 5) {
    datasets = [
      {
        label: t('automatic'),
        data: mode_automatic,
        borderColor: '#004E78',
        backgroundColor: 'rgba(0, 123, 255, 0.1)',
        tension: 0.4,
        pointStyle: 'circle',
        pointRadius: 1.2,
        pointHoverRadius: 7,
      },
      {
        label: t('pages.reports.graphs.activeMode'),
        data: mode_intention,
        borderColor: '#13DFB6',
        backgroundColor: 'rgba(19, 223, 182, 0.1)',
        tension: 0.4,
        pointStyle: 'circle',
        pointRadius: 1.2,
        pointHoverRadius: 7,
      },
    ];
  } else if (deviceModel === 6) {
    datasets = [
      {
        label: t('automatic'),
        data: mode_automatic,
        borderColor: '#004E78',
        backgroundColor: 'rgba(0, 123, 255, 0.1)',
        tension: 0.4,
        pointStyle: 'circle',
        pointRadius: 1.2,
        pointHoverRadius: 7,
      },
      {
        label: t('pages.reports.tables.intention'),
        data: mode_intention,
        borderColor: '#13DFB6',
        backgroundColor: 'rgba(19, 223, 182, 0.1)',
        tension: 0.4,
        pointStyle: 'circle',
        pointRadius: 1.2,
        pointHoverRadius: 7,
      },
    ];
  } else {
    datasets = [
      {
        label: t('automatic'),
        data: mode_automatic,
        borderColor: '#004E78',
        backgroundColor: 'rgba(0, 123, 255, 0.1)',
        tension: 0.4,
        pointStyle: 'circle',
        pointRadius: 1.2,
        pointHoverRadius: 7,
      },
      {
        label: t('intention'),
        data: mode_intention,
        borderColor: '#13DFB6',
        backgroundColor: 'rgba(19, 223, 182, 0.1)',
        tension: 0.4,
        pointStyle: 'circle',
        pointRadius: 1.2,
        pointHoverRadius: 7,
      },
    ];
    // Si el rol no es ROLES.PERSONAL, se añaden los modos de asistencia
    if (canViewAssistanceModes) {
      datasets.push(
        {
          label: `${t('assistance_as_needed')} 1`,
          data: mode_aan1,
          borderColor: '#e32597ff',
          backgroundColor: 'rgba(227, 37, 151, 0.1)',
          tension: 0.4,
          pointStyle: 'circle',
          pointRadius: 1.2,
          pointHoverRadius: 7,
        },
        {
          label: `${t('assistance_as_needed')} 2`,
          data: mode_aan2,
          borderColor: '#f0a500',
          backgroundColor: 'rgba(240, 165, 0, 0.1)',
          tension: 0.4,
          pointStyle: 'circle',
          pointRadius: 1.2,
          pointHoverRadius: 7,
        }
      );
    }
  }

  const dataStepsMode: ChartData = {
    labels,
    datasets,
  };

  const optionsStepsMode: ChartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    aspectRatio: 1,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          color: textColor,
          padding: 20,
        },
      },
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      datalabels: {
        display: false,
      },
    },

    scales: {
      x: {
        display: true,
        ticks: {
          color: textColorSecondary,
          autoSkip: true,
          maxRotation: 45,
        },
        grid: {
          display: false,
          color: surfaceBorder,
        },
      },
      y: {
        ticks: {
          color: textColorSecondary,
          maxTicksLimit: 8,
          stepSize: 100,
        },
        grid: {
          color: surfaceBorder,
        },
        title: {
          display: true,
          text: t('pages.patients.viewPatient.sessions.amount'),
          font: {
            size: 14, // Tamaño de la fuente de la etiqueta
            weight: 'bold', // Peso de la fuente de la etiqueta
          },
        },
      },
    },
  };

  return {
    dataStepsMode,
    optionsStepsMode,
  };
}

// Time graph = DIRECTION - LINEAR
export function getDataTimeDirectionGraphq(
  data: any,
  documentStyle: CSSStyleDeclaration,
  t: TFunction<'translation', undefined>,
  textColorSecondary: string,
  textColor: string,
  surfaceBorder: string,
  deviceModel?: DeviceModelType,
  timeZone?: string
) {
  const _sessions: sessions[] = data;

  // Ordenar por fecha
  _sessions.sort((a, b) => {
    return new Date(a.date).getTime() - new Date(b.date).getTime();
  });

  const labels = _sessions.map((s) =>
    formatSessionDate(s.date, timeZone, {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
    })
  );

  // Direcciones
  const direction_forward = _sessions.map(
    (s) => s.time_automatic_forward + s.time_intention_forward
  );

  const direction_backward = _sessions.map(
    (s) => s.time_automatic_backward + s.time_intention_backward
  );

  const dataTimeDirection: ChartData = {
    labels,
    datasets: [
      {
        label: t('forward'),
        data: direction_forward,
        backgroundColor: 'rgba(0, 78, 120, 0.1)', // Color del área
        borderColor: '#004E78',

        tension: 0.4, // Suavizar la línea
        pointStyle: 'circle', // Estilo de los puntos
        pointRadius: 1.2,
        pointHoverRadius: 7,
      },
      {
        label: t('backward'),
        data: direction_backward,
        backgroundColor: 'rgba(19, 223, 182, 0.1)',
        borderColor: '#13DFB6',
        tension: 0.4,
        pointStyle: 'circle',
        pointRadius: 1.2,
        pointHoverRadius: 7,
      },
    ],
  };

  const optionsTimeDirection: ChartOptions = {
    maintainAspectRatio: false, // Desactivar proporción fija
    responsive: true,
    aspectRatio: 1,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          color: textColor,
          padding: 10,
        },
      },
      // @ts-ignore
      datalabels: {
        display: false, // Desactiva las etiquetas sobre los puntos
      },
    },
    scales: {
      x: {
        ticks: {
          color: textColorSecondary,
          autoSkip: true,
          maxRotation: 45,
        },
        grid: {
          display: false,
          color: surfaceBorder,
        },
      },
      y: {
        ticks: {
          display: true,
          color: textColorSecondary,
          maxTicksLimit: 8,
        },
        grid: {
          display: true,
          color: surfaceBorder,
          lineWidth: 1, // Ancho del grid
        },
        title: {
          display: true,
          text: t('pages.patients.viewPatient.sessions.minutes'),
          font: {
            size: 14,
            weight: 'bold',
          },
        },
      },
    },
  };

  return {
    dataTimeDirection,
    optionsTimeDirection,
  };
}

export function getDataTimeMode(
  data: any,
  role: Role,
  documentStyle: CSSStyleDeclaration,
  t: TFunction<'translation', undefined>,
  textColorSecondary: string,
  textColor: string,
  surfaceBorder: string,
  deviceModel?: DeviceModelType,
  timeZone?: string
) {
  const _sessions: sessions[] = data;
  const canViewAssistanceModes = can(
    role,
    AvailableResourceActions.visualization.view_assistance_modes_graphs
  );

  // Ordenar por fecha
  _sessions.sort((a, b) => {
    return new Date(a.date).getTime() - new Date(b.date).getTime();
  });

  const labels = _sessions.map((s) =>
    formatSessionDate(s.date, timeZone, {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
    })
  );

  // Filtrado de modos según el modelo de dispositivo
  const isAtlas = deviceModel === 5;
  const isExplorerPersonal = deviceModel === 6;

  const mode_automatic = _sessions.map(
    (s) => s.time_automatic_backward + s.time_automatic_forward
  );
  const mode_intention = _sessions.map(
    (s) => s.time_intention_backward + s.time_intention_forward
  );
  let mode_aan1: number[] = [];
  let mode_aan2: number[] = [];
  if (!isAtlas && !isExplorerPersonal && canViewAssistanceModes) {
    mode_aan1 = _sessions.map(
      (s) => (s.time_aan1_backward ?? 0) + (s.time_aan1_forward ?? 0)
    );
    mode_aan2 = _sessions.map(
      (s) => (s.time_aan2_backward ?? 0) + (s.time_aan2_forward ?? 0)
    );
  }

  let datasets: ChartDataset[] = [];
  // Si es Atlas, solo mostrar Automático y Modo activo (no intención ni asistencias)
  if (deviceModel === 5) {
    datasets = [
      {
        label: t('automatic'),
        data: mode_automatic,
        borderColor: '#004E78',
        backgroundColor: 'rgba(0, 123, 255, 0.1)',
        tension: 0.4,
        pointStyle: 'circle',
        pointRadius: 1.2,
        pointHoverRadius: 7,
      },
      {
        label: t('pages.reports.graphs.activeMode'), // Modo activo
        data: mode_intention,
        borderColor: '#13DFB6',
        backgroundColor: 'rgba(19, 223, 182, 0.1)',
        tension: 0.4,
        pointStyle: 'circle',
        pointRadius: 1.2,
        pointHoverRadius: 7,
      },
    ];
  } else if (deviceModel === 6) {
    datasets = [
      {
        label: t('automatic'),
        data: mode_automatic,
        borderColor: '#004E78',
        backgroundColor: 'rgba(0, 123, 255, 0.1)',
        tension: 0.4,
        pointStyle: 'circle',
        pointRadius: 1.2,
        pointHoverRadius: 7,
      },
      {
        label: t('pages.reports.tables.intention'),
        data: mode_intention,
        borderColor: '#13DFB6',
        backgroundColor: 'rgba(19, 223, 182, 0.1)',
        tension: 0.4,
        pointStyle: 'circle',
        pointRadius: 1.2,
        pointHoverRadius: 7,
      },
    ];
  } else {
    datasets = [
      {
        label: t('automatic'),
        data: mode_automatic,
        borderColor: '#004E78',
        backgroundColor: 'rgba(0, 123, 255, 0.1)',
        tension: 0.4,
        pointStyle: 'circle',
        pointRadius: 1.2,
        pointHoverRadius: 7,
      },
      {
        label: t('intention'),
        data: mode_intention,
        borderColor: '#13DFB6',
        backgroundColor: 'rgba(19, 223, 182, 0.1)',
        tension: 0.4,
        pointStyle: 'circle',
        pointRadius: 1.2,
        pointHoverRadius: 7,
      },
    ];
    // Si el rol no es ROLES.PERSONAL, se añaden los modos de asistencia
    if (canViewAssistanceModes) {
      datasets.push(
        {
          label: `${t('assistance_as_needed')} 1`,
          data: mode_aan1,
          borderColor: '#e32597ff',
          backgroundColor: 'rgba(227, 37, 151, 0.1)',
          tension: 0.4,
          pointStyle: 'circle',
          pointRadius: 1.2,
          pointHoverRadius: 7,
        },
        {
          label: `${t('assistance_as_needed')} 2`,
          data: mode_aan2,
          borderColor: '#f0a500',
          backgroundColor: 'rgba(240, 165, 0, 0.1)',
          tension: 0.4,
          pointStyle: 'circle',
          pointRadius: 1.2,
          pointHoverRadius: 7,
        }
      );
    }
  }

  const dataTimeMode: ChartData = {
    labels,
    datasets,
  };

  const optionsTimeMode: ChartOptions = {
    maintainAspectRatio: false, // Desactivar proporción fija
    responsive: true,
    aspectRatio: 1,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          color: textColor,
          padding: 20,
        },
      },
      // @ts-ignore
      datalabels: {
        display: false, // Desactiva las etiquetas sobre los puntos
      },
    },
    scales: {
      x: {
        display: true,
        ticks: {
          color: textColorSecondary,
          autoSkip: true,
          maxRotation: 45,
        },
        grid: {
          display: false,
          color: surfaceBorder,
        },
      },
      y: {
        ticks: {
          color: textColorSecondary,
          maxTicksLimit: 8,
          stepSize: 10,
        },
        grid: {
          color: surfaceBorder,
          lineWidth: 1, // Ancho del grid
        },
        title: {
          display: true,
          text: t('pages.patients.viewPatient.sessions.minutes'),
          font: {
            size: 14,
            weight: 'bold',
          },
        },
      },
    },
  };

  return {
    dataTimeMode,
    optionsTimeMode,
  };
}

// Cadence graph - LINEAR - DIRECTION
export function getDataCadenceDirectionGraph(
  data: any,
  documentStyle: CSSStyleDeclaration,
  t: TFunction<'translation', undefined>,
  textColorSecondary: string,
  textColor: string,
  surfaceBorder: string
) {
  const _sessions: sessions[] = data;

  _sessions.sort((a, b) => {
    return new Date(a.date).getTime() - new Date(b.date).getTime();
  });
  const labels = _sessions.map((s) =>
    new Date(s.date).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
    })
  );

  // Direction: cadencia de todos los modos en cada dirección (pasos ÷ minutos)
  const direction_forward = _sessions.map((s) =>
    getCadence([s], ['automatic_forward', 'intention_forward'])
  );
  const direction_backward = _sessions.map((s) =>
    getCadence([s], ['automatic_backward', 'intention_backward'])
  );

  const dataCadenceDirection: ChartData = {
    labels,
    datasets: [
      {
        label: t('forward'),
        data: direction_forward,
        backgroundColor: 'rgba(0, 78, 120, 0.1)', // Color del área
        borderColor: '#004E78',

        tension: 0.4, // Suavizar la línea
        pointStyle: 'circle', // Estilo de los puntos
        pointRadius: 1.2,
        pointHoverRadius: 7,
      },
      {
        label: t('backward'),
        data: direction_backward,
        backgroundColor: 'rgba(19, 223, 182, 0.1)',
        borderColor: '#13DFB6',
        tension: 0.4,
        pointStyle: 'circle',
        pointRadius: 1.2,
        pointHoverRadius: 7,
      },
    ],
  };
  const optionsCadenceDirection: ChartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    aspectRatio: 1,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          color: textColor,
          padding: 20,
        },
      },
      // @ts-ignore
      datalabels: {
        display: false,
      },
    },

    scales: {
      x: {
        ticks: {
          color: textColorSecondary,
          autoSkip: true,
          maxRotation: 45,
        },
        grid: {
          display: false,
          color: surfaceBorder,
        },
      },
      y: {
        ticks: {
          color: textColorSecondary,
          maxTicksLimit: 8,
          stepSize: 4,
        },
        grid: {
          display: true,
          color: surfaceBorder,
        },
        title: {
          display: true,
          text: t('pages.patients.viewPatient.sessions.stepsPerMinute'),
          font: {
            size: 14, // Tamaño de la fuente de la etiqueta
            weight: 'bold', // Peso de la fuente de la etiqueta
          },
        },
      },
    },
  };

  return {
    dataCadenceDirection,
    optionsCadenceDirection,
  };
}

// Cadence graph - LINEAR - MODE
export function getDataCadenceModeGraph(
  data: any,
  role: Role,
  documentStyle: CSSStyleDeclaration,
  t: TFunction<'translation', undefined>,
  textColorSecondary: string,
  textColor: string,
  surfaceBorder: string,
  deviceModel?: DeviceModelType
) {
  const _sessions: sessions[] = data;
  const canViewAssistanceModes = can(
    role,
    AvailableResourceActions.visualization.view_assistance_modes_graphs
  );

  _sessions.sort((a, b) => {
    return new Date(a.date).getTime() - new Date(b.date).getTime();
  });
  const labels = _sessions.map((s) =>
    new Date(s.date).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
    })
  );

  // Filtrado de modos según el modelo de dispositivo
  const isAtlas = deviceModel === 5;
  const isExplorerPersonal = deviceModel === 6;

  // Mode: cadencia de cada modo juntando las dos direcciones (pasos ÷ minutos)
  const mode_automatic = _sessions.map((s) =>
    getCadence([s], ['automatic_forward', 'automatic_backward'])
  );
  const mode_intention = _sessions.map((s) =>
    getCadence([s], ['intention_forward', 'intention_backward'])
  );
  let mode_aan1: number[] = [];
  let mode_aan2: number[] = [];
  if (!isAtlas && !isExplorerPersonal && canViewAssistanceModes) {
    // Antes se leían los campos cadence_aan1_* / cadence_aan2_*, que processSessions no calcula.
    // Ahora salen de los pasos y tiempos, que sí están.
    mode_aan1 = _sessions.map((s) => getCadence([s], ['aan1_forward', 'aan1_backward']));
    mode_aan2 = _sessions.map((s) => getCadence([s], ['aan2_forward', 'aan2_backward']));
  }

  let datasets: ChartDataset[] = [];
  // Si es Atlas, solo mostrar Automático y Modo activo (no intención ni asistencias)
  if (deviceModel === 5) {
    datasets = [
      {
        label: t('automatic'),
        data: mode_automatic,
        borderColor: '#004E78',
        backgroundColor: 'rgba(0, 123, 255, 0.1)',
        tension: 0.4,
        pointStyle: 'circle',
        pointRadius: 1.2,
        pointHoverRadius: 7,
      },
      {
        label: t('pages.reports.graphs.activeMode'), // Modo activo
        data: mode_intention,
        borderColor: '#13DFB6',
        backgroundColor: 'rgba(19, 223, 182, 0.1)',
        tension: 0.4,
        pointStyle: 'circle',
        pointRadius: 1.2,
        pointHoverRadius: 7,
      },
    ];
  } else if (deviceModel === 6) {
    datasets = [
      {
        label: t('automatic'),
        data: mode_automatic,
        borderColor: '#004E78',
        backgroundColor: 'rgba(0, 123, 255, 0.1)',
        tension: 0.4,
        pointStyle: 'circle',
        pointRadius: 1.2,
        pointHoverRadius: 7,
      },
      {
        label: t('pages.reports.tables.intention'),
        data: mode_intention,
        borderColor: '#13DFB6',
        backgroundColor: 'rgba(19, 223, 182, 0.1)',
        tension: 0.4,
        pointStyle: 'circle',
        pointRadius: 1.2,
        pointHoverRadius: 7,
      },
    ];
  } else {
    datasets = [
      {
        label: t('automatic'),
        data: mode_automatic,
        borderColor: '#004E78',
        backgroundColor: 'rgba(0, 123, 255, 0.1)',
        tension: 0.4,
        pointStyle: 'circle',
        pointRadius: 1.2,
        pointHoverRadius: 7,
      },
      {
        label: t('intention'),
        data: mode_intention,
        borderColor: '#13DFB6',
        backgroundColor: 'rgba(19, 223, 182, 0.1)',
        tension: 0.4,
        pointStyle: 'circle',
        pointRadius: 1.2,
        pointHoverRadius: 7,
      },
    ];

    if (canViewAssistanceModes) {
      datasets.push(
        {
          label: `${t('assistance_as_needed')} 1`,
          data: mode_aan1,
          borderColor: '#e32597ff',
          backgroundColor: 'rgba(227, 37, 151, 0.1)',
          tension: 0.4,
          pointStyle: 'circle',
          pointRadius: 1.2,
          pointHoverRadius: 7,
        },
        {
          label: `${t('assistance_as_needed')} 2`,
          data: mode_aan2,
          borderColor: '#f0a500',
          backgroundColor: 'rgba(240, 165, 0, 0.1)',
          tension: 0.4,
          pointStyle: 'circle',
          pointRadius: 1.2,
          pointHoverRadius: 7,
        }
      );
    }
  }

  const dataCadenceMode: ChartData = {
    labels,
    datasets,
  };
  const optionsCadenceMode: ChartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    aspectRatio: 1,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          color: textColor,
          padding: 20,
        },
      },
      // @ts-ignore
      datalabels: {
        display: false,
      },
    },

    scales: {
      x: {
        display: true,
        ticks: {
          color: textColorSecondary,
          autoSkip: true,
          maxRotation: 45,
        },
        grid: {
          display: false,
          color: surfaceBorder,
        },
      },
      y: {
        ticks: {
          color: textColorSecondary,
          maxTicksLimit: 8,
          stepSize: 4,
        },
        grid: {
          color: surfaceBorder,
        },
        title: {
          display: true,
          text: t('pages.patients.viewPatient.sessions.stepsPerMinute'),
          font: {
            size: 14, // Tamaño de la fuente de la etiqueta
            weight: 'bold', // Peso de la fuente de la etiqueta
          },
        },
      },
    },
  };

  return {
    dataCadenceMode,
    optionsCadenceMode,
  };
}

// Steps graph - BARS - MODE
//Uso del dispositivo
export function getNumberOfStepMode(
  data: any,
  documentStyle: CSSStyleDeclaration,
  t: TFunction<'translation', undefined>,
  deviceModel?: number
) {
  const sessionByMonth = getSessionsByMonth(data);
  const labels = sessionByMonth.map((s) =>
    new Date(s.month_string).toLocaleDateString('es-ES', {
      month: 'short',
    })
  );

  const steps_automatic_forward = sessionByMonth.map((s) =>
    s.sessions.reduce((a, b) => a + b.steps_automatic_forward, 0)
  );

  const steps_automatic_backward = sessionByMonth.map((s) =>
    s.sessions.reduce((a, b) => a + b.steps_automatic_backward, 0)
  );

  const steps_intention_backward = sessionByMonth.map((s) =>
    s.sessions.reduce((a, b) => a + b.steps_intention_backward, 0)
  );

  const steps_intention_forward = sessionByMonth.map((s) =>
    s.sessions.reduce((a, b) => a + b.steps_intention_forward, 0)
  );

  //nuevo para aan1 y aan2
  const steps_aan1_forward = sessionByMonth.map((s) =>
    s.sessions.reduce((a, b) => a + (b.steps_aan1_forward ?? 0), 0)
  );
  const steps_aan1_backward = sessionByMonth.map((s) =>
    s.sessions.reduce((a, b) => a + (b.steps_aan1_backward ?? 0), 0)
  );

  const steps_aan2_forward = sessionByMonth.map((s) =>
    s.sessions.reduce((a, b) => a + (b.steps_aan2_forward ?? 0), 0)
  );
  const steps_aan2_backward = sessionByMonth.map((s) =>
    s.sessions.reduce((a, b) => a + (b.steps_aan2_backward ?? 0), 0)
  );

  const createDataset = (label: string, data: number[], color: string) => ({
    label,
    data,
    backgroundColor: data.map(() => color),
    borderColor: color,
  });
  // Filtrado de modos según el modelo de dispositivo
  let datasets: any[] = [];

  switch (deviceModel) {
    case DeviceModelType.ATLAS:
      // Atlas: solo automático e intención (modo activo)
      datasets = [
        createDataset(
          t('pages.reports.graphs.automaticForward'),
          steps_automatic_forward,
          '#004E78'
        ),
        createDataset(
          t('pages.reports.graphs.automaticBackward'),
          steps_automatic_backward,
          '#DF8F13'
        ),
        createDataset(
          t('pages.reports.graphs.activeModeForward'),
          steps_intention_forward,
          '#13DFB6'
        ),
        createDataset(
          t('pages.reports.graphs.activeModeBackward'),
          steps_intention_backward,
          '#DF1351'
        ),
      ];
      break;

    case DeviceModelType.EXPLORER_PERSONAL:
      // Explorer personal: automático + intención
      datasets = [
        createDataset(
          t('pages.reports.graphs.automaticForward'),
          steps_automatic_forward,
          '#004E78'
        ),
        createDataset(
          t('pages.reports.graphs.automaticBackward'),
          steps_automatic_backward,
          '#DF8F13'
        ),
        createDataset(
          t('pages.reports.graphs.forwardIntention'),
          steps_intention_forward,
          '#13DFB6'
        ),
        createDataset(
          t('pages.reports.graphs.backwardIntention'),
          steps_intention_backward,
          '#DF1351'
        ),
      ];
      break;

    case DeviceModelType.EXPLORER_CLINICO:
    default:
      // Explorer clínico u otros: todos los modos
      datasets = [
        createDataset(
          t('pages.reports.graphs.automaticForward'),
          steps_automatic_forward,
          '#004E78'
        ),
        createDataset(
          t('pages.reports.graphs.automaticBackward'),
          steps_automatic_backward,
          '#DF8F13'
        ),
        createDataset(
          t('pages.reports.graphs.forwardIntention'),
          steps_intention_forward,
          '#13DFB6'
        ),
        createDataset(
          t('pages.reports.graphs.backwardIntention'),
          steps_intention_backward,
          '#DF1351'
        ),
        createDataset(
          t('pages.reports.graphs.ann1Forward'),
          steps_aan1_forward,
          '#8e24aa'
        ),
        createDataset(
          t('pages.reports.graphs.ann1Backward'),
          steps_aan1_backward,
          '#9eb135'
        ),
        createDataset(
          t('pages.reports.graphs.ann2Forward'),
          steps_aan2_forward,
          '#00897b'
        ),
        createDataset(
          t('pages.reports.graphs.ann2Backward'),
          steps_aan2_backward,
          '#6d4c41'
        ),
      ];
      break;
  }

  const dataNumberOfSteps: ChartData = {
    labels,
    datasets,
  };

  const optionsNumberOfSteps: ChartOptions = {
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 10,
        },
      },
      // @ts-ignore
      datalabels: {
        display: false,
      },
    },
    scales: {
      y: {
        title: {
          display: true,
          text: t('pages.reports.graphs.steps'),
          font: {
            size: 14,
          },
        },
      },
      x: {
        stacked: false,
      },
    },
  };

  return {
    dataNumberOfSteps,
    optionsNumberOfSteps,
  };
}

// Time graph - BARS - MODE
//Uso del dispositivo (Tiempo de uso)
export function getNumberOfTimeMode(
  data: any,
  t: TFunction<'translation', undefined>,
  deviceModel?: number,
  timeZone?: string
) {
  const sessionByMonth = getSessionsByMonth(data, timeZone);
  const labels = sessionByMonth.map((s) =>
    formatSessionDate(s.month_string, timeZone, {
      month: 'short',
    })
  );

  const time_intention_forward = sessionByMonth.map((s) =>
    s.sessions.reduce((a, b) => a + b.time_intention_forward, 0)
  );

  const time_automatic_forward = sessionByMonth.map((s) =>
    s.sessions.reduce((a, b) => a + b.time_automatic_forward, 0)
  );

  const time_intention_backward = sessionByMonth.map((s) =>
    s.sessions.reduce((a, b) => a + b.time_intention_backward, 0)
  );

  const time_automatic_backward = sessionByMonth.map((s) =>
    s.sessions.reduce((a, b) => a + b.time_automatic_backward, 0)
  );
  //nuevas para aan1 y aan2
  const time_aan1_forward = sessionByMonth.map((s) =>
    s.sessions.reduce((a, b) => a + (b.time_aan1_forward ?? 0), 0)
  );
  const time_aan1_backward = sessionByMonth.map((s) =>
    s.sessions.reduce((a, b) => a + (b.time_aan1_backward ?? 0), 0)
  );
  const time_aan2_forward = sessionByMonth.map((s) =>
    s.sessions.reduce((a, b) => a + (b.time_aan2_forward ?? 0), 0)
  );
  const time_aan2_backward = sessionByMonth.map((s) =>
    s.sessions.reduce((a, b) => a + (b.time_aan2_backward ?? 0), 0)
  );

  const createDataset = (label: string, data: number[], color: string) => ({
    label,
    data,
    backgroundColor: data.map(() => color),
    borderColor: color,
  });
  // Filtrado de modos según el modelo de dispositivo
  let datasets: any[] = [];

  switch (deviceModel) {
    case DeviceModelType.ATLAS:
      // Atlas: solo automático e intención (modo activo)
      datasets = [
        createDataset(
          t('pages.reports.graphs.automaticForward'),
          time_automatic_forward,
          '#004E78'
        ),
        createDataset(
          t('pages.reports.graphs.automaticBackward'),
          time_automatic_backward,
          '#DF8F13'
        ),
        createDataset(
          t('pages.reports.graphs.activeModeForward'),
          time_intention_forward,
          '#13DFB6'
        ),
        createDataset(
          t('pages.reports.graphs.activeModeBackward'),
          time_intention_backward,
          '#DF1351'
        ),
      ];
      break;

    case DeviceModelType.EXPLORER_PERSONAL:
      // Explorer personal: automático + intención
      datasets = [
        createDataset(
          t('pages.reports.graphs.automaticForward'),
          time_automatic_forward,
          '#004E78'
        ),
        createDataset(
          t('pages.reports.graphs.automaticBackward'),
          time_automatic_backward,
          '#DF8F13'
        ),
        createDataset(
          t('pages.reports.graphs.forwardIntention'),
          time_intention_forward,
          '#13DFB6'
        ),
        createDataset(
          t('pages.reports.graphs.backwardIntention'),
          time_intention_backward,
          '#DF1351'
        ),
      ];
      break;

    case DeviceModelType.EXPLORER_CLINICO:
    default:
      // Explorer clínico u otros: todos los modos
      datasets = [
        createDataset(
          t('pages.reports.graphs.automaticForward'),
          time_automatic_forward,
          '#004E78'
        ),
        createDataset(
          t('pages.reports.graphs.automaticBackward'),
          time_automatic_backward,
          '#DF8F13'
        ),
        createDataset(
          t('pages.reports.graphs.forwardIntention'),
          time_intention_forward,
          '#13DFB6'
        ),
        createDataset(
          t('pages.reports.graphs.backwardIntention'),
          time_intention_backward,
          '#DF1351'
        ),
        createDataset(
          t('pages.reports.graphs.ann1Forward'),
          time_aan1_forward,
          '#8e24aa'
        ),
        createDataset(
          t('pages.reports.graphs.ann1Backward'),
          time_aan1_backward,
          '#9eb135'
        ),
        createDataset(
          t('pages.reports.graphs.ann2Forward'),
          time_aan2_forward,
          '#00897b'
        ),
        createDataset(
          t('pages.reports.graphs.ann2Backward'),
          time_aan2_backward,
          '#6d4c41'
        ),
      ];
      break;
  }

  const dataNumberOfTime: ChartData = {
    labels,
    datasets,
  };

  const optionsTimeOfUse: ChartOptions = {
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 10,
        },
      },
      // @ts-ignore
      datalabels: {
        display: false,
      },
    },
    responsive: true,
    scales: {
      y: {
        grid: { display: true },
        title: {
          display: true,
          text: t('pages.reports.graphs.time'),
          font: {
            size: 14,
            weight: 'bold',
          },
        },
      },
      x: {
        grid: { display: false },
        stacked: false,
      },
    },
  };

  return {
    dataNumberOfTime,
    optionsTimeOfUse,
  };
}

// Total steps - HOME
export function getTotalSteps(
  data: any,
  documentStyle: CSSStyleDeclaration,
  t: TFunction<'translation', undefined>,
  _surfaceBorder: string,
  _textColorSecondary: string
) {
  const sessionByMonth = getSessionsByMonth(data);

  const _sessions: sessions[] =
    sessionByMonth[sessionByMonth.length - 1]?.sessions || [];

  _sessions.sort((a, b) => {
    return new Date(a.date).getTime() - new Date(b.date).getTime();
  });

  // Agrupar sesiones por día y sumarizar el número total de pasos
  const sessionsByDay: Record<string, number> = _sessions.reduce(
    (result: Record<string, number>, session) => {
      const dateKey = new Date(session.date).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });

      if (!result[dateKey]) {
        result[dateKey] = 0;
      }

      result[dateKey] += session.steps_total;

      return result;
    },
    {}
  );

  // Obtener etiquetas y datos sumarizados y ordenarlos
  const sortedEntries = Object.entries(sessionsByDay).sort(
    (a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime()
  );
  const labels = sortedEntries.map(([dateKey]) => dateKey);
  const total_data = sortedEntries.map(([_, total]) => total);

  const dataNumberOfSteps: ChartData = {
    labels,
    datasets: [
      {
        data: total_data,
        fill: false,
        borderColor: documentStyle.getPropertyValue(
          '--forward-intention-color-1'
        ),
        backgroundColor: 'rgba(40, 167, 69, 0.1)',
        tension: 0.4,
        pointStyle: 'circle',
        pointRadius: 1.2,
        pointHoverRadius: 7,
      },
    ],
  };
  const optionsNumberOfSteps: ChartOptions = {
    maintainAspectRatio: true,
    aspectRatio: 1,
    plugins: {
      // @ts-ignore
      datalabels: {
        display: false,
      },
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const day = context.label;
            const value = context.parsed.y;
            return `${day}  - ${value} ${t('steps')}`;
          },
        },
      },
    },
    layout: {
      padding: {
        top: 32,
        right: 16,
        bottom: 16,
        left: 8,
      },
    },

    scales: {
      x: {
        display: true,
        grid: {
          display: false,
        },
        ticks: {
          autoSkip: true,
          maxRotation: 45,
          maxTicksLimit: 12,
        },
      },
      y: {
        display: true,
        title: {
          display: true,
          text: t('pages.reports.graphs.steps'),
          font: {
            size: 14,
          },
        },
        ticks: {
          maxTicksLimit: 8,
          stepSize: 200,
        },
      },
    },
  };

  return {
    dataNumberOfSteps,
    optionsNumberOfSteps,
  };
}

// Total time use - HOME
export function getTotalTime(
  data: any,
  documentStyle: CSSStyleDeclaration,
  t: TFunction<'translation', undefined>,
  _urfaceBorder: string,
  _textColorSecondary: string
) {
  const sessionByMonth = getSessionsByMonth(data);

  const _sessions: sessions[] =
    sessionByMonth[sessionByMonth.length - 1]?.sessions || [];

  // Agrupar sesiones por día y sumarizar la duración total de uso
  const sessionsByDay: Record<string, number> = _sessions.reduce(
    (result: Record<string, number>, session) => {
      const dateKey = new Date(session.date).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });

      if (!result[dateKey]) {
        result[dateKey] = 0;
      }

      result[dateKey] += Number(session?.timeSession?.toFixed(2) ?? 0);

      return result;
    },
    {}
  );

  // Obtener etiquetas y datos sumarizados y ordenarlos
  const sortedEntries = Object.entries(sessionsByDay).sort(
    (a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime()
  );
  const labels = sortedEntries.map(([dateKey]) => dateKey);
  const total_data = sortedEntries.map(([_, total]) => total);

  const dataTimeOfUse: ChartData = {
    labels,
    datasets: [
      {
        data: total_data,
        fill: false,
        borderColor: '#004E78',
        backgroundColor: 'rgba(0, 123, 255, 0.1)',
        tension: 0.4,
        pointStyle: 'circle',
        pointRadius: 1.2,
        pointHoverRadius: 7,
      },
    ],
  };
  const optionsTimeOfUse: ChartOptions = {
    maintainAspectRatio: true,
    aspectRatio: 1,
    plugins: {
      // @ts-ignore
      datalabels: {
        display: false,
      },
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const day = context.label;
            const value = context.parsed.y;
            return `${day} - ${value} min`;
          },
        },
      },
    },
    layout: {
      padding: {
        top: 32,
        right: 16,
        bottom: 16,
        left: 8,
      },
    },

    scales: {
      x: {
        display: true,
        grid: {
          display: false,
        },
        ticks: {
          autoSkip: true,
          maxRotation: 45,
          maxTicksLimit: 12,
        },
      },
      y: {
        display: true,
        title: {
          display: true,
          text: t('pages.reports.graphs.time'),
          font: {
            size: 14,
            weight: 'bold',
          },
        },
        ticks: {
          maxTicksLimit: 8,
          stepSize: 5,
        },
      },
    },
  };

  return {
    dataTimeOfUse,
    optionsTimeOfUse,
  };
}

// Graph of flexos/time = INTRASESSION (Circular)
export function getFlexosTimeGraph(
  data: any,
  dataType: 'steps' | 'time',
  flexoType: 'FlexosH' | 'FlexosK',
  t: TFunction<'translation', undefined>
) {
  // Convert time from minutes to milliseconds. Se deja de usar por incongruencia con el tiempo total de los flexos.
  //const timeInMilliseconds = minutesToMilliseconds(time);

  // Validación para evitar error si data.sessionParameters es undefined o no es un array
  let sessionParameters = {};
  if (
    Array.isArray(data?.sessionParameters) &&
    data.sessionParameters.length > 0 &&
    data.sessionParameters[0]?.sessionParameters
  ) {
    sessionParameters = data.sessionParameters[0].sessionParameters;
  }

  // Aggregate flexos data, duration, and steps
  const aggregatedFlexosData = aggregateFlexos(sessionParameters, flexoType);

  // Calcular total real de aggregated flexos data en sustitución de timeInMilliseconds
  const totalTime = Object.values(aggregatedFlexosData).reduce(
    (acc, data) => acc + (data.duration || 0),
    0
  );
  //Calcular total de steps
  const totalSteps = Object.values(aggregatedFlexosData).reduce(
    (acc, data) => acc + (data.steps || 0),
    0
  );

  // Convert flexos data to percentage values / total time in milliseconds
  const dataValues = Object.values(aggregatedFlexosData).map((data) =>
    dataType === 'steps'
      ? get_percentage(data.steps, totalSteps)
      : get_percentage(data.duration, totalTime)
  );

  // Si no hay datos, devolver null
  if (dataValues.length === 0 || dataValues.every((value) => value === 0)) {
    return {};
  }

  // Get labels, background COLORS, and hover colors
  const labels = Object.keys(aggregatedFlexosData).map((value) =>
    t('pages.patients.viewPatient.sessions.flexValue', { value })
  );

  const backgroundColor = Object.keys(aggregatedFlexosData).map((_, index) =>
    getTransparentColor(COLORS[index % COLORS.length], 0.1)
  );

  const hoverBackgroundColor = Object.keys(aggregatedFlexosData).map(
    (_, index) => COLORS[index % COLORS.length]
  );

  const borderColor = Object.keys(aggregatedFlexosData).map(
    (_, index) => COLORS[index % COLORS.length]
  );

  // Define chart data
  const dataFlexosTime: ChartData = {
    labels,
    datasets: [
      {
        data: dataValues,
        backgroundColor: backgroundColor,
        hoverBackgroundColor: hoverBackgroundColor,
        borderColor: borderColor,
      },
    ],
  };

  const optionsFlexosTime: ChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 1.2,
    plugins: {
      title: {
        display: true,
        text:
          flexoType === 'FlexosH'
            ? t('pages.patients.viewPatient.sessions.hipFlexionDistribution')
            : t('pages.patients.viewPatient.sessions.kneeFlexionDistribution'),
        font: {
          size: 20,
          weight: 'bold',
        },
      },
      legend: {
        display: true,
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
        },
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const value = dataFlexosTime.datasets[0].data[context.dataIndex];
            const formattedValue = formatNumber(value, i18next.language);
            return `${formattedValue}%`;
          },
        },
      },

      // @ts-ignore
      datalabels: {
        display: false, //showDisplayDataLabels(window?.innerWidth),
        borderColor: 'white',
        borderRadius: 50,
        borderWidth: 2,
        padding: 3,
        font: {
          weight: 'bold',
          size: 20,
        },
        backgroundColor: () => {
          return 'white';
        },
        color: 'black',
        formatter: (value: any) => {
          const formattedValue = formatNumber(value, i18next.language);
          return `${formattedValue}%`;
        },
      },
    },
    scales: {
      x: {
        display: false,
      },
      y: {
        display: false,
      },
    },
  };

  return {
    dataFlexosTime,
    optionsFlexosTime,
  };
}

//FC graph - INTRASESSION (LINEAR)
export function getFCGraphIntra(
  data: Array<{ timestamp: string; heartRate: number }>,
  t: TFunction<'translation', undefined>,
  _birth_date: string | null
) {
  const labels = data.map((s) => s.timestamp);

  // const maxHeartRate = birth_date
  //   ? Math.ceil(208 - 0.7 * calculateAge(birth_date))
  //   : 211;

  const heartRateData = data.map((s) => s.heartRate);

  const dataHR: ChartData = {
    labels,
    datasets: [
      {
        label: t('pages.patients.viewPatient.sessions.fc'),
        data: heartRateData,
        borderColor: '#D9534F',
        tension: 0.4,
        pointStyle: 'circle',
        pointRadius: 1.2,
        pointHoverRadius: 7,
        // @ts-ignore
        datalabels: {
          display: false,
        },
      },
    ],
  };

  const optionsHR: ChartOptions = {
    maintainAspectRatio: true,
    aspectRatio: 1,
    plugins: {
      legend: {
        display: false,
      },
      // @ts-ignore
      datalabels: {
        display: false, // Desactiva las etiquetas sobre los puntos
      },
    },
    scales: {
      x: {
        display: false,
        grid: {
          display: false,
        },
        ticks: {
          autoSkip: true,
          maxRotation: 45,
        },
      },
      y: {
        display: true,
        max: 250,
        beginAtZero: true,
        ticks: {
          maxTicksLimit: 8,
          stepSize: 20,
        },
        title: {
          display: true,
          text: t('pages.patients.viewPatient.sessions.points'),
          font: {
            size: 12,
            weight: 'bold',
          },
        },
      },
    },
  };

  return {
    dataHR,
    optionsHR,
  };
}

// Bar graph average step time - INTRASESSION (BARS - DIRECTION)
export function getBarDistributionGraph(
  data: any,
  title: string,
  t: TFunction<'translation', undefined>
) {
  // Validación para evitar error si data es undefined, null o vacío
  if (!data || typeof data !== 'object' || Object.keys(data).length === 0) {
    return {};
  }

  const labels = Object.keys(data);

  // Average step time
  const barDataset: any = {
    label: t('pages.patients.viewPatient.sessions.avgStepTimeSeconds'),
    data: labels.map((threshold) => data[threshold].averageStepTime),
    backgroundColor: 'rgba(75, 192, 192, 0.5)',
    borderColor: 'rgba(75, 192, 192, 1)',
    borderWidth: 1,
    type: 'bar',
  };

  // Steps forward
  const scatterForwardDataset: any = {
    label: t('pages.patients.viewPatient.sessions.stepsForward'),
    data: labels.flatMap(
      (threshold) =>
        data[threshold]?.steps
          ?.filter((step: any) => step.direction !== 1)
          .map((step: any) => ({
            x: threshold,
            y: step.stepTime,
          })) ?? []
    ),
    backgroundColor: 'rgb(4, 56, 90)',
    radius: 4,
    type: 'scatter',
  };

  // Steps backward
  const scatterBackwardDataset: any = {
    label: t('pages.patients.viewPatient.sessions.stepsBackward'), // ← otra etiqueta
    data: labels.flatMap(
      (threshold) =>
        data[threshold]?.steps
          ?.filter((step: any) => step.direction === 1)
          .map((step: any) => ({
            x: threshold,
            y: step.stepTime,
          })) ?? []
    ),
    backgroundColor: 'rgb(206, 9, 52)',
    radius: 4,
    type: 'scatter',
  };

  const dataBarDistribution: any = {
    labels,
    datasets: [barDataset, scatterForwardDataset, scatterBackwardDataset],
  };

  const optionsBarDistribution: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 10,
        },
      },
      datalabels: {
        display: false,
      },
      title: {
        display: true,
        text: title,
        font: {
          size: 20,
          weight: 'bold',
        },
      },
      tooltip: {
        callbacks: {
          label: (tooltipItem: any) => {
            if (tooltipItem.dataset.type === 'scatter') {
              const directionLabel = tooltipItem.dataset.label;
              return (
                t('pages.patients.viewPatient.sessions.time') +
                ': ' +
                tooltipItem.raw.y +
                ' - ' +
                directionLabel
              );
            }
            return (
              t('pages.patients.viewPatient.sessions.avgStepTimeSeconds') +
              ': ' +
              tooltipItem.raw.toFixed(2)
            );
          },
        },
      },
    },
    scales: {
      x: {
        stacked: false,
        title: {
          display: true,
          text: t('pages.patients.viewPatient.sessions.thresholdConfiguration'),
          font: {
            size: 14,
          },
        },
        ticks: {
          callback: (value: any) => {
            const [hip, knee] = labels[value].split(',');
            return `${t(
              'pages.patients.viewPatient.sessions.hip'
            )} ${hip.trim()} ${t(
              'pages.patients.viewPatient.sessions.knee'
            )} ${knee.trim()}`;
          },
        },
      },
      y: {
        stacked: true,
        title: {
          display: true,
          text: t('pages.patients.viewPatient.sessions.seconds'),
          font: {
            size: 14,
          },
        },
      },
    },
  };

  return {
    dataBarDistribution,
    optionsBarDistribution,
  };
}

// Distribution of steps last session - REPORT
export function getStepSessionGraph(
  data: any,
  documentStyle: CSSStyleDeclaration,
  t: TFunction<'translation', undefined>,
  window: any,
  legend: boolean
) {
  const dataStepsLastSessionDistribution: ChartData = {
    labels: [
      t('pages.reports.graphs.automaticForward'),
      t('pages.reports.graphs.automaticBackward'),
      t('pages.reports.graphs.backwardIntention'),
      t('pages.reports.graphs.forwardIntention'),
    ],
    datasets: [
      {
        data: [
          get_percentage(data.steps_automatic_forward, data.steps_total),
          get_percentage(data.steps_automatic_backward, data.steps_total),
          get_percentage(data.steps_intention_backward, data.steps_total),
          get_percentage(data.steps_intention_forward, data.steps_total),
        ],
        backgroundColor: [
          getTransparentColor(
            documentStyle.getPropertyValue('--forward-automatic-color-1'),
            0.1
          ),
          getTransparentColor(
            documentStyle.getPropertyValue('--backward-automatic-color-1'),
            0.1
          ),
          getTransparentColor(
            documentStyle.getPropertyValue('--backward-intention-color-1'),
            0.1
          ),
          getTransparentColor(
            documentStyle.getPropertyValue('--forward-intention-color-1'),
            0.1
          ),
          getTransparentColor(
            documentStyle.getPropertyValue('--aan1-forward-color-1'),
            0.1
          ),
          // getTransparentColor(
          //   documentStyle.getPropertyValue('--aan1-backward-color-1'),
          //   0.1
          // ),
          // getTransparentColor(
          //   documentStyle.getPropertyValue('--aan2-forward-color-1'),
          //   0.1
          // ),
          // getTransparentColor(
          //   documentStyle.getPropertyValue('--aan2-backward-color-1'),
          //   0.1
          // ),
        ],
        hoverBackgroundColor: [
          documentStyle.getPropertyValue('--forward-automatic-color-2'),
          documentStyle.getPropertyValue('--backward-automatic-color-2'),
          documentStyle.getPropertyValue('--backward-intention-color-2'),
          documentStyle.getPropertyValue('--forward-intention-color-2'),
          // documentStyle.getPropertyValue('--aan1-forward-color-2'),
          // documentStyle.getPropertyValue('--aan1-backward-color-2'),
          // documentStyle.getPropertyValue('--aan2-forward-color-2'),
          // documentStyle.getPropertyValue('--aan2-backward-color-2'),
        ],
        borderColor: [
          documentStyle.getPropertyValue('--forward-automatic-color-1'),
          documentStyle.getPropertyValue('--backward-automatic-color-1'),
          documentStyle.getPropertyValue('--backward-intention-color-1'),
          documentStyle.getPropertyValue('--forward-intention-color-1'),
          // documentStyle.getPropertyValue('--aan1-forward-color-1'),
          // documentStyle.getPropertyValue('--aan1-backward-color-1'),
          // documentStyle.getPropertyValue('--aan2-forward-color-1'),
          // documentStyle.getPropertyValue('--aan2-backward-color-1'),
        ],
      },
    ],
  };
  const optionsStepsLastSessionDistribution: ChartOptions = {
    maintainAspectRatio: true,
    aspectRatio: 1.5,
    plugins: {
      legend: {
        display: legend,
        position: 'top',
        labels: {
          pointStyle: 'circle',
        },
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            // @ts-ignore
            const value =
              dataStepsLastSessionDistribution.datasets[0].data[
              context.dataIndex
              ];
            // Valor formateado que cambia según el idioma.
            const formattedValue = formatNumber(value, i18next.language);
            return `${formattedValue}%`;
          },
        },
      },
      // @ts-ignore
      datalabels: {
        display: showDisplayDataLabels(window?.innerWidth),
        borderColor: 'white',
        borderRadius: 50,
        borderWidth: 2,
        padding: 3,
        font: {
          weight: 'bold',
        },
        backgroundColor: (_context: any) => {
          return 'white';
        },
        color: 'black',
        formatter: (value: any, _context: any) => {
          // @ts-ignore
          // Valor formateado que cambia según el idioma.
          const formattedValue = formatNumber(value, i18next.language);
          return `${formattedValue}%`;
        },
      },
    },
  };

  return {
    dataStepsLastSessionDistribution,
    optionsStepsLastSessionDistribution,
  };
}

// Steps graph = DIRECTION - DOES NOT USE
export function getNumberOfStepDirection(
  data: any,
  documentStyle: CSSStyleDeclaration,
  t: TFunction<'translation', undefined>
) {
  const sessionByMonth = getSessionsByMonth(data);
  const labels = sessionByMonth.map((s) =>
    new Date(s.month_string).toLocaleDateString('es-ES', {
      month: 'short',
    })
  );

  const steps_intention_forward = sessionByMonth.map((s) =>
    s.sessions.reduce((a, b) => a + b.steps_intention_forward, 0)
  );

  const steps_automatic_forward = sessionByMonth.map((s) =>
    s.sessions.reduce((a, b) => a + b.steps_automatic_forward, 0)
  );

  const steps_intention_backward = sessionByMonth.map((s) =>
    s.sessions.reduce((a, b) => a + b.steps_intention_backward, 0)
  );

  const steps_automatic_backward = sessionByMonth.map((s) =>
    s.sessions.reduce((a, b) => a + b.steps_automatic_backward, 0)
  );

  const dataNumberOfStepsDirection: ChartData = {
    labels,
    datasets: [
      {
        label: t('pages.reports.graphs.automaticForward'),
        data: steps_automatic_forward,
        backgroundColor: steps_automatic_forward.map(() => '#004E78'), // 🔹 Array de colores
        borderColor: '#004E78',
      },
      {
        label: t('pages.reports.graphs.automaticBackward'),
        data: steps_automatic_backward,
        backgroundColor: steps_automatic_backward.map(() => '#13DFB6'), // 🔹 Array de colores
        borderColor: '#13DFB6',
      },
      {
        label: t('pages.reports.graphs.forwardIntention'),
        data: steps_intention_forward,
        backgroundColor: steps_intention_forward.map(() => '#DF8F13'), // 🔹 Array de colores
        borderColor: '#DF8F13',
      },
      {
        label: t('pages.reports.graphs.backwardIntention'),
        data: steps_intention_backward,
        backgroundColor: steps_intention_backward.map(() => '#DF1351'), // 🔹 Array de colores
        borderColor: '#DF1351',
      },
    ],
  };

  const optionsNumberOfStepsDirection: ChartOptions = {
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
        },
      },
      // @ts-ignore
      datalabels: {
        display: false,
      },
    },
    scales: {
      y: {
        title: {
          display: true,
          text: t('pages.reports.graphs.steps'),
          font: {
            size: 14,
          },
        },
      },
      x: {
        stacked: false,
      },
    },
  };

  return {
    dataNumberOfStepsDirection,
    optionsNumberOfStepsDirection,
  };
}

//=============INTERFACES=================//

// Interface properties for the sessions data
export interface SessionData {
  // Total steps
  total_steps_automatic_forward: number;
  total_steps_automatic_backward: number;
  total_steps_intention_forward: number;
  total_steps_intention_backward: number;

  // NUEVAS variables para ann1 y ann2
  total_steps_aan1_forward: number;
  total_steps_aan1_backward: number;
  total_steps_aan2_forward: number;
  total_steps_aan2_backward: number;

  total_steps_automatic: number;
  total_steps_intention: number;
  total_steps_aan1: number;
  total_steps_aan2: number;

  total_steps_forward: number;
  total_steps_backward: number;

  // Total time
  total_time_automatic_forward: number;
  total_time_automatic_backward: number;
  total_time_intention_forward: number;
  total_time_intention_backward: number;

  // NUEVAS variables de tiempo para ANN1 y ANN2
  total_time_aan1_forward: number;
  total_time_aan1_backward: number;
  total_time_aan2_forward: number;
  total_time_aan2_backward: number;

  total_time_automatic: number;
  total_time_intention: number;
  total_time_aan1: number;
  total_time_aan2: number;
  total_time_forward: number;
  total_time_backward: number;

  total_steps_total: number;
  total_time_total: number;

  // Median steps
  median_total_steps: number;
  median_total_steps_automatic_forward: number;
  median_total_steps_automatic_backward: number;
  median_total_steps_intention_forward: number;
  median_total_steps_intention_backward: number;
  median_total_steps_automatic: number;
  median_total_steps_intention: number;
  median_total_steps_forward: number;
  median_total_steps_backward: number;
  median_total_time: number;
  median_total_time_automatic_forward: number;
  median_total_time_automatic_backward: number;
  median_total_time_intention_forward: number;
  median_total_time_intention_backward: number;
  median_total_time_automatic: number;
  median_total_time_intention: number;
  median_total_time_forward: number;
  median_total_time_backward: number;
  evaluation: number;
  cadence_automatic_forward: number;
  cadence_automatic_backward: number;
  cadence_intention_forward: number;
  cadence_intention_backward: number;
  flexos_hip: number;
  flexos_knee: number;
  flexos_ankle: number;
  threshold_hipl: number;
  threshold_hipr: number;
  threshold_kneel: number;
  threshold_kneer: number;
  effort: number;
  total_time_use: number;
  total_time_walking: number;
  total_time_standing: number;
  torque_hipl: number;
  torque_hipr: number;
  torque_kneel: number;
  torque_kneer: number;
  rom_hipl: number;
  rom_hipr: number;
  rom_kneel: number;
  rom_kneer: number;
  fc_max: number;
  fc_med: number;
  so2: number;
}

// Interface properties for the table of last session data
export interface TableScoreLastSessionDataInterface {
  automaticSteps: {
    forward: {
      result: number;
      index: number;
    };
    backward: {
      result: number;
      index: number;
    };
  };
  intentionalSteps: {
    forward: {
      result: number;
      index: number;
    };
    backward: {
      result: number;
      index: number;
    };
  };
  cadence: {
    automatic_forward: {
      result: number;
      index: number;
    };
    automatic_backward: {
      result: number;
      index: number;
    };
    intentional_forward: {
      result: number;
      index: number;
    };
    intentional_backward: {
      result: number;
      index: number;
    };
  };
  flexos: {
    hip: {
      result: number;
      index: number;
    };
    knee: {
      result: number;
      index: number;
    };
  };

  threshold: {
    hipL: {
      result: number;
      index: number;
    };
    kneeL: {
      result: number;
      index: number;
    };
    hipR: {
      result: number;
      index: number;
    };
    kneeR: {
      result: number;
      index: number;
    };
  };

  therapist: {
    dungarees: {
      result: number;
      index: number;
    };
    effort: {
      result: number;
      index: number;
    };
  };

  total: number;
}

// Interface properties for the comparative table of last session data against historical and latest monthly data
export interface ComparativeTableOfLastSessionDataAgainstHistoricalAndLatestMonthlyDataInterface {
  automaticSteps: {
    historical: {
      forward: {
        result: number;
        index: number;
      };
      backward: {
        result: number;
        index: number;
      };
    };
    lastMonth: {
      forward: {
        result: number;
        index: number;
      };
      backward: {
        result: number;
        index: number;
      };
    };
    lastSession: {
      forward: {
        result: number;
        index: number;
      };
      backward: {
        result: number;
        index: number;
      };
    };
  };
  intentionalSteps: {
    historical: {
      forward: {
        result: number;
        index: number;
      };
      backward: {
        result: number;
        index: number;
      };
    };
    lastMonth: {
      forward: {
        result: number;
        index: number;
      };
      backward: {
        result: number;
        index: number;
      };
    };
    lastSession: {
      forward: {
        result: number;
        index: number;
      };
      backward: {
        result: number;
        index: number;
      };
    };
  };
  cadence: {
    historical: {
      automatic: {
        result: number;
        index: number;
      };
      intentional: {
        result: number;
        index: number;
      };
    };
    lastMonth: {
      automatic: {
        result: number;
        index: number;
      };
      intentional: {
        result: number;
        index: number;
      };
    };
    lastSession: {
      automatic: {
        result: number;
        index: number;
      };
      intentional: {
        result: number;
        index: number;
      };
    };
  };
  flexos: {
    historical: {
      hip: {
        result: number;
        index: number;
      };
      knee: {
        result: number;
        index: number;
      };
    };
    lastMonth: {
      hip: {
        result: number;
        index: number;
      };
      knee: {
        result: number;
        index: number;
      };
    };
    lastSession: {
      hip: {
        result: number;
        index: number;
      };
      knee: {
        result: number;
        index: number;
      };
    };
  };
  threshold: {
    historical: {
      hipl: {
        result: number;
        index: number;
      };
      kneel: {
        result: number;
        index: number;
      };
      hipr: {
        result: number;
        index: number;
      };
      kneer: {
        result: number;
        index: number;
      };
    };
    lastMonth: {
      hipl: {
        result: number;
        index: number;
      };
      kneel: {
        result: number;
        index: number;
      };
      hipr: {
        result: number;
        index: number;
      };
      kneer: {
        result: number;
        index: number;
      };
    };
    lastSession: {
      hipl: {
        result: number;
        index: number;
      };
      kneel: {
        result: number;
        index: number;
      };
      hipr: {
        result: number;
        index: number;
      };
      kneer: {
        result: number;
        index: number;
      };
    };
  };
  therapist: {
    historical: {
      dungarees: {
        result: number;
        index: number;
      };
      effort: {
        result: number;
        index: number;
      };
    };
    lastMonth: {
      dungarees: {
        result: number;
        index: number;
      };
      effort: {
        result: number;
        index: number;
      };
    };
    lastSession: {
      dungarees: {
        result: number;
        index: number;
      };
      effort: {
        result: number;
        index: number;
      };
    };
  };
  totalHistorical: number;
  totalLastMonth: number;
  totalLastSession: number;
}

//==============AUXILIARY FUNCTIONS============================//
/**
 * Function to aggregate duration and steps for flexos with the same value
 *
 * @param {Record<string, any>} data - Object containing multiple keys, each with flexos data
 * @param {string} flexoType - Type of flexos to process ('FlexosH' or 'FlexosK')
 * @return {Record<number, { duration: number; steps: number }>} - Aggregated data
 */

export function aggregateFlexos(
  data: Record<string, any>,
  flexoType: 'FlexosH' | 'FlexosK'
): Record<number, { duration: number; steps: number }> {
  const result: Record<number, { duration: number; steps: number }> = {};

  // Iterar sobre las claves del objeto
  Object.keys(data).forEach((key) => {
    const entry = data[key];
    const flexos = entry[flexoType] ?? []; // Asegurarse de que el tipo de flexos exista

    // Iterar sobre cada elemento del tipo de flexos
    flexos.forEach(
      ({
        value,
        duration,
        steps,
      }: {
        value: number;
        duration: number;
        steps: number;
      }) => {
        if (!result[value]) {
          result[value] = { duration: 0, steps: 0 };
        }

        // Acumular duración y pasos
        result[value].duration += duration;
        result[value].steps += steps;
      }
    );
  });

  return result;
}
/**
 * Function to convert minutes to milliseconds
 *
 * @param {number} minutes - Minutes to convert
 * @returns {number} - Milliseconds
 */
export function minutesToMilliseconds(minutes: number): number {
  return Math.round(minutes * 60 * 1000);
}
/**
 * Function to get the name of the month in Spanish
 *
 * @param {number} month - Número del mes
 * @return {string} - Nombre del mes
 */
export function getMonthName(month: number) {
  const months = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio ',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ];
  return months[month];
}

/**
 * Funciton to format a number based on the language
 *
 * @param {number | [number, number] | Point | BubbleDataPoint | null} value - Valor a formatear
 * @param {string} language - Idioma
 */
export function formatNumber(
  value: number | [number, number] | Point | BubbleDataPoint | null,
  language: string
): string | string[] {
  if (Array.isArray(value)) {
    return value.map((v) => formatNumber(v, language)) as string[];
  }

  if (value instanceof Object) {
    console.warn(
      'formatNumber: Received an object, returning an empty string for compatibility.'
    );
    return '';
  }

  if (typeof value === 'number') {
    if (Number.isInteger(value)) {
      return value.toLocaleString(language);
    }

    return value.toLocaleString(language, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  return '';
}

/**
 * Function to parse values to number or return 0 if not possible
 *
 * @param {any} value - Value to parse
 * @return {number} - Parsed number or 0
 */
const safeNumber = (value: any) =>
  typeof value === 'number' ? value : parseFloat(value) || 0;

/**
 * Cadencia (pasos/min) de un conjunto de sesiones para los modos indicados.
 * La cadencia es un ritmo: nunca se suman ni se promedian cadencias.
 * Se suman pasos y minutos de todo lo que se agrupa y se divide al final.
 *
 * @param sessions - Sesiones (o días ya agrupados) a combinar
 * @param modes - Modo + dirección, p. ej. ['automatic_forward', 'intention_forward']
 * @return Cadencia redondeada a 1 decimal, o 0 si no hubo tiempo caminando
 */
export function getCadence(sessions: any[], modes: string[]): number {
  let steps = 0;
  let minutes = 0;

  for (const session of sessions) {
    for (const mode of modes) {
      steps += safeNumber(session[`steps_${mode}`]);
      minutes += safeNumber(session[`time_${mode}`]);
    }
  }

  if (minutes <= 0) return 0;
  return Math.round((steps / minutes) * 10) / 10;
}

/**
 * Function to get the background color of an element
 *
 * @param {string} color - Background color
 * @param {number} opacity - Opacity value (0-1)
 * @return {string} - RGBA color string
 */
const getTransparentColor = (color: string, opacity: number) => {
  if (color.startsWith('rgb')) {
    return color.replace(')', `, ${opacity})`).replace('rgb', 'rgba');
  }
  return color;
};

/**
 * Function to show or not show data labels based on screen width
 *
 * @param {number} width - Width of the screen
 * @return {boolean} - True if data labels should be shown, false otherwise
 */
export function showDisplayDataLabels(width: number) {
  if (width >= 450 && width < 1199) {
    return true;
  } else if (width <= 449) {
    return false;
  } else {
    return true;
  }
}

/**
 * Get the first and last session from a list of sessions
 *
 * @export
 * @param {any[]} sessions
 * @return {*}
 */
export function getEarlyAndLastSession(sessions: any[]) {
  const sortedSessions = sessions.sort((a, b) => {
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });

  const earlySession = sortedSessions[sortedSessions.length - 1];
  const lastSession = sortedSessions[0];

  return {
    earlySession,
    lastSession,
  };
}

/**
 * Get the first and last alarm from a list of alarms
 *
 * @export
 * @param {any[]} sessions
 * @return {*}
 */
export function getEarlyAndLastAlarm(alarms: any[]) {
  const sortedAlarms = alarms.sort((a, b) => {
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });

  const earlyAlarm = sortedAlarms[sortedAlarms.length - 1];
  const lastAlarm = sortedAlarms[0];

  return {
    earlyAlarm,
    lastAlarm,
  };
}

function calculateAge(birthDateString: string | Date): number {
  const today = new Date();
  const birthDate = new Date(birthDateString);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }

  return age;
}

//funcion para panel principal
export interface SessionForClinicianStepsRanking {
  u_id?: string | null;
  steps_total?: number | null;

  // Por si alguna sesión no trae steps_total
  steps_automatic_forward?: number | null;
  steps_automatic_backward?: number | null;
  steps_intention_forward?: number | null;
  steps_intention_backward?: number | null;
  steps_aan1_forward?: number | null;
  steps_aan1_backward?: number | null;
  steps_aan2_forward?: number | null;
  steps_aan2_backward?: number | null;
}

function getTotalStepsFromSession(session: SessionForClinicianStepsRanking) {
  if (typeof session.steps_total === 'number') {
    return session.steps_total;
  }

  return (
    (session.steps_automatic_forward ?? 0) +
    (session.steps_automatic_backward ?? 0) +
    (session.steps_intention_forward ?? 0) +
    (session.steps_intention_backward ?? 0) +
    (session.steps_aan1_forward ?? 0) +
    (session.steps_aan1_backward ?? 0) +
    (session.steps_aan2_forward ?? 0) +
    (session.steps_aan2_backward ?? 0)
  );
}

//Funcion para obtener el ranking de pasos por clínico y generar los datos para la gráfica de Panel Principal
export function getClinicianStepsRankingGraph(
  sessions: SessionForClinicianStepsRanking[],
  t: TFunction<'translation', undefined>,
  topLimit?: number
) {
  if (!sessions || sessions.length === 0) {
    return {
      dataClinicianStepsRanking: {
        labels: [],
        datasets: [],
      },
      optionsClinicianStepsRanking: {},
      ranking: [],
    };
  }

  const rankingMap = new Map<string, number>();

  sessions.forEach((session) => {
    if (!session.u_id) return;

    const totalSteps = getTotalStepsFromSession(session);

    rankingMap.set(
      session.u_id,
      (rankingMap.get(session.u_id) ?? 0) + totalSteps
    );
  });

  let ranking = Array.from(rankingMap.entries())
    .map(([u_id, totalSteps]) => ({
      u_id,
      totalSteps,
    }))
    .sort((a, b) => b.totalSteps - a.totalSteps);

  if (topLimit && topLimit > 0) {
    ranking = ranking.slice(0, topLimit);
  }

  const labels = ranking.map((item, index) => `${index + 1}. ${item.u_id}`);
  const values = ranking.map((item) => item.totalSteps);

  const colors = labels.map(
    (_, index) => `hsl(${(index * 47) % 360}, 70%, 60%)`
  );

  const dataClinicianStepsRanking: ChartData = {
    labels,
    datasets: [
      {
        label: t('pages.reports.graphs.steps'),
        data: values,
        backgroundColor: colors,
        borderColor: colors,
        borderWidth: 1,
        borderRadius: 5,
      },
    ],
  };

  const optionsClinicianStepsRanking: ChartOptions = {
    indexAxis: 'y',
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const value = context.parsed.x ?? 0;

            return `${t('pages.reports.graphs.steps')}: ${value.toLocaleString()}`;
          },
        },
      },
      // @ts-ignore
      datalabels: {
        display: false,
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          precision: 0,
        },
        title: {
          display: true,
          text: t('pages.reports.graphs.steps'),
          font: {
            size: 14,
            weight: 'bold',
          },
        },
      },
      y: {
        grid: {
          display: false,
        },
      },
    },
  };

  return {
    dataClinicianStepsRanking,
    optionsClinicianStepsRanking,
    ranking,
  };
}
